// src/ManifestScreen.jsx
import { useState, useEffect, useRef } from "react";
import { api } from "./api/api";
import generateBolInvoicePdf from "./generateBolInvoicePdf";
import generateAirCargoManifestPdf from "./generatManifest";

// ✅ تعريف الثوابت المفقودة
const DEFAULT_GENERAL = {
  customsOfficeCode: "SDPZU",
  modeOfTransportCode: "4", // Air transport
  transporterNationality: "SD",
  placeOfDepartureCode: "SDPZU",
  placeOfDestinationCode: "KRT",
};

// ✅ دالة تحويل الأحرف الخاصة للـ XML
function escapeXml(value) {
  if (value == null) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

// ✅ دالة تسطيح البوالص لحساب المجاميع
const flattenBols = (masters) => {
  const res = [];
  (masters || []).forEach((m) => {
    res.push(m);
    if (Array.isArray(m.homes)) {
      m.homes.forEach((h) => res.push(h));
    }
  });
  return res;
};

// ✅ تواريخ المدخلات
const toDateInput = (d) => {
  if (!d) return "";
  const date = new Date(d);
  if (isNaN(date.getTime())) return String(d).slice(0, 10);
  // استخدام الدوال المحلية لتجنب الانزياح الزمني (Timezone Shift)
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Helper to parse JSON arrays or single IDs from DB
const parseIdArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value == null || value === "") return [];
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed;
      return [parsed];
    } catch {
      return trimmed.split(",").map((item) => item.trim()).filter(Boolean);
    }
  }
  return [value];
};
// ✅ حساب الأيام
const calculateDays = (start, end, subtractDay) => {
  if (!start || !end) return 0;

  
  // دالة داخلية لتحويل أي مدخل لتاريخ محلي صافي بدون ساعات
  const parseToLocal = (d) => {
    const dt = new Date(d);
    return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
  };

  const s = parseToLocal(start);
  const e = parseToLocal(end);
  
  // إضافة +1 ليكون الحساب شاملاً لليوم الأول والأخير (Inclusive) كما في التقارير
  let diff = Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1;
  if (subtractDay) diff -= 1;
  return Math.max(0, diff);
};

// ✅ تعريف مكون الحقل المفقود (Field Component)
function Field({ label, children, error }) {
  return (
    <div className={`field ${error ? "has-error" : ""}`}>
      <label>{label}</label>
      {children}
    </div>
  );
}

// ✅ دالة مساعدة لجلب النص من عناصر XML
const getText = (node, selector) => node?.querySelector(selector)?.textContent || "";

// ✅ تعريف قائمة الرحلات المحددة مسبقاً (يمكنك إضافة بيانات إليها لاحقاً)
const PREDEFINED_TRIPS = [];

function LegacyManifestScreen({ onBack }) {
  const [paidBols, setPaidBols] = useState([]);
  const [selectedBols, setSelectedBols] = useState([]);
  const [voyages, setVoyages] = useState([]);
  const [selectedVoyage, setSelectedVoyage] = useState(null);
  const [manifest, setManifest] = useState({
    voyageNumber: '',
    dateOfDeparture: '',
    dateOfArrival: '',
    storageStartDate: '',
    storageEndDate: '',
    bols: []
  });
  const [freePeriods, setFreePeriods] = useState([]);
  const [storageItems, setStorageItems] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    await Promise.all([
      fetchPaidBols(),
      fetchVoyages(),
      fetchFreePeriods(),
      fetchStorage()
    ]);
  };

// ✅ تحويل fetchPaidBols لدالة مستقلة قابلة للاستدعاء
const fetchPaidBols = async () => {
  try {
    const res = await api.get('/bols');
    setPaidBols(res.filter(b => b.paid === 1));
  } catch (err) {
    console.error('Error fetching paid bols:', err);
  }
};

// useEffect يستدعيها عند التحميل
useEffect(() => {
  fetchPaidBols();
}, []);



  const fetchVoyages = async () => {
    try {
      const res = await api.get('/voyages');
      setVoyages(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error('Error fetching voyages:', err);
    }
  };

  const fetchFreePeriods = async () => {
    try {
      const res = await api.get('/free-periods');
      setFreePeriods(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error('Error fetching free periods:', err);
    }
  };

  const fetchStorage = async () => {
    try {
      const res = await api.get('/storage');
      setStorageItems(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error('Error fetching storage:', err);
    }
  };

  const selectVoyage = (voyage) => {
    setSelectedVoyage(voyage);
    setManifest({
      voyageNumber: voyage.voyage_number,
      dateOfDeparture: voyage.date_of_departure,
      dateOfArrival: voyage.date_of_arrival,
      storageStartDate: voyage.storage_start_date,
      storageEndDate: voyage.storage_end_date,
      bols: []
    });
  };

  const toggleBolSelection = (bol) => {
    setSelectedBols(prev =>
      prev.find(b => b.id === bol.id)
        ? prev.filter(b => b.id !== bol.id)
        : [...prev, bol]
    );
  };

  const assignBolsToManifest = () => {
    if (!selectedVoyage) {
      alert('Please select a voyage first');
      return;
    }
    setManifest(prev => ({
      ...prev,
      bols: selectedBols.map(bol => ({
        ...bol,
        storageInvoice: calculateStorageInvoice(bol)
      }))
    }));
    setSelectedBols([]);
  };

  const calculateStorageInvoice = (bol) => {
    const freePeriod = freePeriods.find(fp => fp.type === bol.cargo_type)?.free_period || 0;
    const storageDays = calculateDays(bol.storage_start_date, bol.storage_end_date, bol.day);
    const freeDays = parseInt(freePeriod) || 0;
    const chargeableDays = Math.max(0, storageDays - freeDays);

    const storageRate = storageItems.find(s => s.type === bol.cargo_type);
    if (!storageRate) return { days: chargeableDays, amount: 0 };

    let amount = 0;
    if (storageRate.is_fixed) {
      amount = storageRate.price * chargeableDays;
    } else {
      amount = storageRate.price * bol.gross_mass * chargeableDays;
    }

    return { days: chargeableDays, amount };
  };

  const saveManifest = async () => {
    if (!manifest.voyageNumber || manifest.bols.length === 0) {
      alert('Please select voyage and assign BOLs');
      return;
    }
    try {
      const payload = {
        voyage_number: manifest.voyageNumber,
        date_of_departure: manifest.dateOfDeparture,
        date_of_arrival: manifest.dateOfArrival,
        storage_start_date: manifest.storageStartDate,
        storage_end_date: manifest.storageEndDate,
        bols: manifest.bols.map(bol => ({
          bol_id: bol.id,
          storage_invoice: bol.storageInvoice
        }))
      };
      await api.post('/manifests', payload);
      alert('Manifest saved successfully');
      // Reset or navigate
    } catch (err) {
      console.error('Error saving manifest:', err);
      alert('Error saving manifest');
    }
  };

  const exportXml = () => {
    // Placeholder for XML export
    alert('Exporting XML for manifest');
  };

  return (
    <div className="page">
      <h2>Manifest Creation</h2>

      <div className="card">
        <h3>Select Voyage</h3>
        <table className="grid-table">
          <thead>
            <tr>
              <th>Voyage Number</th>
              <th>Departure</th>
              <th>Arrival</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {voyages.map(v => (
              <tr key={v.id}>
                <td>{v.voyage_number}</td>
                <td>{v.date_of_departure}</td>
                <td>{v.date_of_arrival}</td>
                <td>
                  <button className="btn btn-primary" onClick={() => selectVoyage(v)}>Select</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedVoyage && (
        <div className="card">
          <h3>Selected Voyage: {manifest.voyageNumber}</h3>
          <p>Departure: {manifest.dateOfDeparture} | Arrival: {manifest.dateOfArrival}</p>
        </div>
      )}

      <div className="card">
        <h3>Paid BOLs</h3>
        <table className="grid-table">
          <thead>
            <tr>
              <th>Select</th>
              <th>BOL Reference</th>
              <th>Consignee</th>
              <th>Cargo Type</th>
              <th>Weight</th>
              <th>Storage Days</th>
            </tr>
          </thead>
          <tbody>
            {paidBols.map(bol => (
              <tr key={bol.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedBols.some(b => b.id === bol.id)}
                    onChange={() => toggleBolSelection(bol)}
                  />
                </td>
                <td>{bol.bol_reference}</td>
                <td>{bol.consignee_name}</td>
                <td>{bol.cargo_type}</td>
                <td>{bol.gross_mass}</td>
                <td>{calculateDays(bol.storage_start_date, bol.storage_end_date, bol.day)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="actions-row">
          <button className="btn btn-primary" onClick={assignBolsToManifest}>Assign to Manifest</button>
        </div>
      </div>

      <div className="card">
        <h3>Manifest BOLs</h3>
        <table className="grid-table">
          <thead>
            <tr>
              <th>BOL Reference</th>
              <th>Consignee</th>
              <th>Address</th>
              <th>Chargeable Days</th>
              <th>Storage Amount</th>
            </tr>
          </thead>
          <tbody>
            {manifest.bols.map(bol => (
              <tr key={bol.id}>
                <td>{bol.bol_reference}</td>
                <td>{bol.consignee_name}</td>
                <td style={{ fontSize: '10px' }}>{bol.consignee_address}</td>
                <td>{bol.storageInvoice.days}</td>
                <td>${bol.storageInvoice.amount.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="actions-row">
          <button className="btn btn-primary" onClick={saveManifest}>Save Manifest</button>
          <button className="btn" onClick={exportXml}>Export XML</button>
        </div>
      </div>
    </div>
  );
}



// Calculate totals from BOLs list
function computeTotalsFromBols(masters) {
  let totalBols = 0;
  let totalPackages = 0;
  let totalContainers = 0;
  let totalGrossMass = 0;

  (masters || []).forEach((m) => {
    // إذا كانت البوليصة مستبعدة (Offloaded)، لا تدخل في الحساب
    if (m.is_offloaded) return;

    if (Array.isArray(m.homes) && m.homes.length > 0) {
      // بوليصة مجمعة: نجمع بيانات الهاوسات فقط ونستثني الماستر
      m.homes.forEach((h) => {
        if (h.is_offloaded) return;
        totalBols++;
        totalPackages += Number(h.numberOfPackages || h.number_of_packages || 0);
        totalContainers += Number(h.numOfCtnForThisBol || h.num_of_ctn_for_this_bol || 0);
        totalGrossMass += Number(h.grossMass || h.gross_mass || 0);
      });
    } else {
      // بوليصة مباشرة (Direct): نجمع بيانات الماستر
      totalBols++;
      totalPackages += Number(m.numberOfPackages || m.number_of_packages || 0);
      totalContainers += Number(m.numOfCtnForThisBol || m.num_of_ctn_for_this_bol || 0);
      totalGrossMass += Number(m.grossMass || m.gross_mass || 0);
    }
  });

  return {
    totalBols: String(totalBols),
    totalPackages: String(totalPackages),
    totalContainers: String(totalContainers),
    totalGrossMass: String(totalGrossMass),
  };
}

// Convert manifest object → AWmds XML text
function manifestToAwmdsXml(manifest) {
  const g = manifest.general || {};
  const masters = manifest.bols || [];
  const bols = [];
 

  (masters || []).forEach((m) => {
    bols.push({ ...m, __isHouse: false, __masterRef: "" });
    const homes = Array.isArray(m.homes) ? m.homes : [];
    homes.forEach((h) => {
      bols.push({ ...h, __isHouse: true, __masterRef: m.bolReference || "" });
    });
  });

  let xml = `<?xml version='1.0' encoding='utf-8'?>`;
  let lineCounter = 1;
  xml += `\n<Awmds>\n`;
  xml += `  <General_segment>\n`;
  xml += `    <General_segment_id>\n`;
  xml += `      <Customs_office_code>${escapeXml(g.customsOfficeCode)}</Customs_office_code>\n`;
  xml += `      <Voyage_number>${escapeXml(g.voyageNumber)}</Voyage_number>\n`;
  xml += `      <Date_of_departure>${escapeXml(g.dateOfArrival)}</Date_of_departure>\n`;
  xml += `      <Date_of_arrival>${escapeXml(g.dateOfDeparture || "")}</Date_of_arrival>\n`;
  xml += `    </General_segment_id>\n`;
  xml += `    <Totals_segment>\n`;
  xml += `      <Total_number_of_bols>${escapeXml(g.totalBols)}</Total_number_of_bols>\n`;
  xml += `      <Total_number_of_packages>${escapeXml(g.totalPackages)}</Total_number_of_packages>\n`;
  xml += `      <Total_number_of_containers>${escapeXml(g.totalContainers)}</Total_number_of_containers>\n`;
  xml += `      <Total_gross_mass>${escapeXml(g.totalGrossMass)}</Total_gross_mass>\n`;
  xml += `    </Totals_segment>\n`;
  xml += `    <Transport_information>\n`;
  xml += `      <Carrier>\n`;
  xml += `        <Carrier_code>00123</Carrier_code>\n`;
  xml += `        <Carrier_name>${escapeXml(g.carrierName)}</Carrier_name>\n`;
  xml += `        <Carrier_address>${escapeXml(g.carrierAddress)}</Carrier_address>\n`;
  xml += `      </Carrier>\n`;
  xml += `      <Shipping_Agent>\n`;
  xml += `        <Shipping_Agent_code>${escapeXml(g.shippingAgentCode)}</Shipping_Agent_code>\n`;
  xml += `        <Shipping_Agent_name>${escapeXml(g.shippingAgentName)}</Shipping_Agent_name>\n`;
  xml += `      </Shipping_Agent>\n`;
  xml += `      <Mode_of_transport_code>${escapeXml(g.modeOfTransportCode)}</Mode_of_transport_code>\n`;
  xml += `      <Identity_of_transporter>${escapeXml(g.identityOfTransporter)}</Identity_of_transporter>\n`;
  xml += `      <Nationality_of_transporter_code>${escapeXml(g.transporterNationality)}</Nationality_of_transporter_code>\n`;
  xml += `    </Transport_information>\n`;
  xml += `    <Load_unload_place>\n`;
  xml += `      <Place_of_departure_code>${escapeXml(g.placeOfDepartureCode)}</Place_of_departure_code>\n`;
  xml += `      <Place_of_destination_code>${escapeXml(g.placeOfDestinationCode)}</Place_of_destination_code>\n`;
  xml += `    </Load_unload_place>\n`;
  xml += `    <Storage_information>\n`;
  xml += `      <Storage_start_date>${escapeXml(g.storageStartDate)}</Storage_start_date>\n`;
  xml += `      <Storage_end_date>${escapeXml(g.storageEndDate)}</Storage_end_date>\n`;
  xml += `      <Storage_days>${escapeXml(g.storageDays)}</Storage_days>\n`;
  xml += `      <Storage_rate>${escapeXml(g.storageRate)}</Storage_rate>\n`;
  xml += `      <Total_storage_charge>${escapeXml(g.totalStorageCharge)}</Total_storage_charge>\n`;
  xml += `      <Total_service_cost>${escapeXml(g.totalServiceCost)}</Total_service_cost>\n`;
  xml += `      <Total_invoice>${escapeXml(g.totalInvoice)}</Total_invoice>\n`;
  xml += `    </Storage_information>\n`;
  xml += `  </General_segment>\n`;

  bols.forEach((bol) => {
    xml += `  <Bol_segment>\n`;
    xml += `    <Bol_id>\n`;
    xml += `      <Bol_reference>${escapeXml(bol.bolReference)}</Bol_reference>\n`;
    xml += `      <Line_number>${lineCounter++}</Line_number>\n`;
    xml += `      <Bol_nature>${escapeXml(bol.bolNature)}</Bol_nature>\n`;
    xml += `      <Bol_type_code>${escapeXml(bol.bolTypeCode)}</Bol_type_code>\n`;
    if (bol.__isHouse) {
      xml += `      <Master_bol_ref_number>${escapeXml(bol.__masterRef || bol.masterBolRefNumber)}</Master_bol_ref_number>\n`;
    }
    xml += `    </Bol_id>\n`;
    xml += `    <Load_unload_place>\n`;
    xml += `      <Place_of_loading_code>${escapeXml(bol.placeOfLoadingCode || g.placeOfDepartureCode)}</Place_of_loading_code>\n`;
    xml += `      <Place_of_unloading_code>${escapeXml(bol.placeOfUnloadingCode || g.placeOfDestinationCode)}</Place_of_unloading_code>\n`;
    xml += `    </Load_unload_place>\n`;
    xml += `    <Traders_segment>\n`;
    xml += `      <Exporter>\n`;
    xml += `        <Exporter_name>${escapeXml(bol.exporterName)}</Exporter_name>\n`;
    xml += `        <Exporter_address>${escapeXml(bol.exporterAddress)}</Exporter_address>\n`;
    xml += `      </Exporter>\n`;
    xml += `      <Service_segment>\n`;
    xml += `        <Service_id>${escapeXml(bol.serviceId)}</Service_id>\n`;
    xml += `      </Service_segment>\n`;
    xml += `      <Notify>\n`;
    xml += `        <Notify_name>${escapeXml(bol.notifyName)}</Notify_name>\n`;
    xml += `        <Notify_address>${escapeXml(bol.notifyAddress)}</Notify_address>\n`;
    xml += `      </Notify>\n`;
    xml += `      <Consignee>\n`;
    xml += `        <Consignee_name>${escapeXml(bol.consigneeName)}</Consignee_name>\n`;
    xml += `        <Consignee_address>${escapeXml(bol.consigneeAddress)}</Consignee_address>\n`;
    xml += `      </Consignee>\n`;
    xml += `    </Traders_segment>\n`;
    xml += `    <Goods_segment>\n`;
    xml += `      <Number_of_packages>${escapeXml(bol.numberOfPackages)}</Number_of_packages>\n`;
    xml += `      <Package_type_code>${escapeXml(bol.packageTypeCode)}</Package_type_code>\n`;
    xml += `      <Gross_mass>${escapeXml(bol.grossMass)}</Gross_mass>\n`;
    xml += `      <Shipping_marks>${escapeXml(bol.shippingMarks)}</Shipping_marks>\n`;
    xml += `      <Goods_description>${escapeXml(bol.goodsDescription)}</Goods_description>\n`;
    xml += `      <Num_of_ctn_for_this_bol>${escapeXml(bol.numOfCtnForThisBol)}</Num_of_ctn_for_this_bol>\n`;
    xml += `    </Goods_segment>\n`;
    xml += `    <Value_segment></Value_segment>\n`;
    xml += `  </Bol_segment>\n`;
  });

  xml += `</Awmds>\n`;
  return xml;
}

// Download XML text as file
function downloadXmlFile(xmlText, fileName = "manifest.xml") {
  const blob = new Blob([xmlText], { type: "application/xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function readPerms() {
  try {
    return JSON.parse(localStorage.getItem("permissions") || "[]");
  } catch {
    return [];
  }
}

function has(perms, code) {
  return Array.isArray(perms) && perms.includes(code);
}

function makeTempId() {
  return `tmp_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

// ✅ Helper to save to localStorage (sqflit equivalent in web)
function saveLocalPair(key, name, address) {
  if (!name || !address) return;
  try {
    const raw = localStorage.getItem(key);
    let list = raw ? JSON.parse(raw) : [];
    const idx = list.findIndex((i) => i.name === name);
    if (idx > -1) {
      list[idx].address = address;
    } else {
      list.push({ name, address });
    }
    localStorage.setItem(key, JSON.stringify(list));
  } catch (e) {
    console.error("Error saving local pair", e);
  }
}

// ✅ Helper to save single values to localStorage
function saveLocalValue(key, value) {
  if (!value || !String(value).trim()) return;
  try {
    const raw = localStorage.getItem(key);
    let list = raw ? JSON.parse(raw) : [];
    const val = String(value).trim();
    const exists = list.some(item => item.name === val);
    if (!exists) {
      list.push({ name: val, address: "" }); // Keep structure for LocalAutocomplete
      localStorage.setItem(key, JSON.stringify(list));
    }
  } catch (e) {
    console.error(`Error saving local value for key ${key}:`, e);
  }
}

// ✅ Local Autocomplete Component
function LocalAutocomplete({ label, value, onChange, onSelect, storageKey, disabled, error }) {
  const [suggestions, setSuggestions] = useState([]);
  const [show, setShow] = useState(false);

  const handleSearch = (query) => {
    if (!query) { setSuggestions([]); return; }
    try {
      const raw = localStorage.getItem(storageKey);
      const list = raw ? JSON.parse(raw) : [];
      const filtered = list.filter((item) => (item.name || "").toLowerCase().includes(query.toLowerCase()));
      setSuggestions(filtered.slice(0, 10));
    } catch (e) { setSuggestions([]); }
  };

  return (
    <div className={`field ${error ? "has-error" : ""}`} style={{ position: "relative" }}>
      <label>{label}</label>

     {/* NEW: Apply conditional style if there is no any value */}




      <input
        value={value || ""}
        disabled={disabled}
        onChange={(e) => { const v = e.target.value; onChange(v); handleSearch(v); setShow(true); }}
        onFocus={() => { handleSearch(value || ""); setShow(true); }}
        onBlur={() => setTimeout(() => setShow(false), 200)}
      />
      {show && suggestions.length > 0 && (
        <ul className="dropdown-list">
          {suggestions.map((s, i) => (
            <li key={i} onMouseDown={() => { onSelect(s); setShow(false); }}>
              <strong>{s.name}</strong>
              <div className="hint">{s.address}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ManifestScreen({ onBack }) {
  const [mode, setMode] = useState("xml");
  const [xmlFile, setXmlFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [showErrors, setShowErrors] = useState(false);
  const [manifestQuery, setManifestQuery] = useState("");
  const [manifestList, setManifestList] = useState([]);
  const [filteredManifests, setFilteredManifests] = useState([]);
  const [showManifestDropdown, setShowManifestDropdown] = useState(false);
  // ✅ Paid BOLs for selection
  const [paidBols, setPaidBols] = useState([]);
  const [showPaidBolsDropdown, setShowPaidBolsDropdown] = useState(false);

  const [selectedManifestId, setSelectedManifestId] = useState(null);
  const [computedTotals, setComputedTotals] = useState({
    storageDays: "0",
    totalServiceCost: "0",
    totalStorageCharge: "0",
    totalInvoice: "0",
  });
  const [showGeneral, setShowGeneral] = useState(true);
  const [voyageQuery, setVoyageQuery] = useState("");
  const [voyageOptions, setVoyageOptions] = useState([]);
  const [showVoyageDropdown, setShowVoyageDropdown] = useState(false);
  const [ownerQuery, setOwnerQuery] = useState("");
  const [ownerOptions, setOwnerOptions] = useState([]);
  const [showOwnerDropdown, setShowOwnerDropdown] = useState(false);
  const [bolStatusQuery, setBolStatusQuery] = useState("");
  const [bolStatusResult, setBolStatusResult] = useState(null);
  const [bolStatusLoading, setBolStatusLoading] = useState(false);
  const [focusedBol, setFocusedBol] = useState(null);
  const [voyageDraft, setVoyageDraft] = useState({
    voyageNumber: "",
    ownerOperator: "",
    customsOfficeCode: ""
  });
  const [servicesList, setServicesList] = useState([]);
  const [packingItems, setPackingItems] = useState([]);
  const [buildUpItems, setBuildUpItems] = useState([]);
  const [storageItems, setStorageItems] = useState([]);
  const [cargoTypes, setCargoTypes] = useState([]);
  const [freePeriods, setFreePeriods] = useState([]);

  // ✅ manifest MUST be defined here before useEffect uses it
  const [manifest, setManifest] = useState({
    general: {
      customsOfficeCode: DEFAULT_GENERAL.customsOfficeCode,
      declaredTotalBols: "",
      voyageNumber: "",
      dateOfDeparture: "",
      dateOfArrival: "",
      consigneeName: "",
      consigneeAddress: "",
      storageStartDate: "",
      storageEndDate: "",
      storageDays: "0",
      storageRate: "0",
      totalBols: "0",
      totalPackages: "0",
      totalContainers: "0",
      totalGrossMass: "0",
      totalServiceCost: "0",
      totalStorageCharge: "0",
      totalInvoice: "0",
      modeOfTransportCode: DEFAULT_GENERAL.modeOfTransportCode,
      transporterNationality: DEFAULT_GENERAL.transporterNationality,
      placeOfDepartureCode: DEFAULT_GENERAL.placeOfDepartureCode,
      placeOfDestinationCode: DEFAULT_GENERAL.placeOfDestinationCode,
      carrierCode: "",
      carrierName: "",
      carrierAddress: "",
      shippingAgentCode: "",
      shippingAgentName: "",
      identityOfTransporter: "",
      ownerOperator: "",
      registration: "",
      flightNo: "",
    },
    bols: [],
  });

  const ignoreScrollRef = useRef(false);

  useEffect(() => {
    const handleScroll = () => {
      if (focusedBol && !ignoreScrollRef.current) {
        setFocusedBol(null);
      }
    };
    const handleClickOutside = (e) => {
      if (focusedBol && !e.target.closest(`#${focusedBol}`)) {
        setFocusedBol(null);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [focusedBol]);

  // ✅ Fetch paid BOLs on load
  useEffect(() => {
// ✅ أرسل paid=true للسيرفر ليفلتر من DB مباشرة
const fetchPaid = async () => {
  const res = await api.get('/bols?paid=true'); // <-- هنا التغيير
  setPaidBols(res); // لا حاجة للفلترة مرة ثانية
};
    fetchPaid();
  }, []);

  const checkBolStatus = async () => {
    if (!bolStatusQuery.trim()) return;
    setBolStatusLoading(true);
    setBolStatusResult(null);
    try {
      const res = await api.get(`/bols/by-reference/${encodeURIComponent(bolStatusQuery.trim())}`);
      setBolStatusResult(res);
    } catch (err) {
      console.error(err);
      setBolStatusResult({ error: "AWB not found or error" });
    } finally {
      setBolStatusLoading(false);
    }
  };

  useEffect(() => {
    if (!showVoyageDropdown) return;
    const t = setTimeout(async () => {
      try {
        const q = (voyageQuery || "").trim();
        const url = q ? `/voyages?q=${encodeURIComponent(q)}` : `/voyages`;
        const rows = await api.get(url);
        setVoyageOptions(rows || []);
      } catch (err) {
        console.error("Error fetching voyages:", err);
        setVoyageOptions([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [voyageQuery, showVoyageDropdown]);

  // ✅ دالة جلب سعر الخدمة بناءً على النوع والوزن مع دعم الهيكل الجديد
const getServicePrice = (bol) => {
  let totalServicePrice = 0;
  const serviceIds = Array.isArray(bol.service_ids) ? bol.service_ids : (bol.serviceId ? [bol.serviceId] : []);

  serviceIds.forEach(sId => {
    const service = servicesList.find((s) => Number(s.id) === Number(sId));
    if (service) {
      const cargoType = (bol.cargoType || "GCR").toLowerCase();
      totalServicePrice += Number(service[cargoType] || service.prices?.[bol.cargoType] || 0);
    }
  });
  return totalServicePrice;
  };

  const getPackingPrice = (bol) => {
    let totalPackingPrice = 0;
    const packingItemIds = Array.isArray(bol.packing_item_ids) ? bol.packing_item_ids : (bol.packingItemId ? [bol.packingItemId] : []);

    packingItemIds.forEach(pId => {
      const item = packingItems.find((p) => Number(p.id) === Number(pId));
      totalPackingPrice += (item ? Number(item.price || 0) : 0);
    });
    return totalPackingPrice;
  };

  const getBuildUpPrice = (bol) => {
    let totalBuildUpPrice = 0;
    const buildUpItemIds = Array.isArray(bol.build_up_item_ids) ? bol.build_up_item_ids : (bol.buildUpItemId ? [bol.buildUpItemId] : []);

    buildUpItemIds.forEach(bId => {
      const item = buildUpItems.find((b) => Number(b.id) === Number(bId));
      totalBuildUpPrice += (item ? Number(item.price || 0) : 0);
    });
    return totalBuildUpPrice;
  };

  const getBolTotal = (bol) => {
        const sPrice = getServicePrice(bol);
    const pPrice = getPackingPrice(bol);
    const bPrice = getBuildUpPrice(bol);
    const weight = Number(bol.grossMass || 0);
    const qty = Number(bol.numberOfPackages || 0);

    return (sPrice * weight) + (pPrice * qty) + bPrice;
  };

  // ✅ دالة لاستخراج رقم الساعات من النص (مثال: "44 hours" -> 44)
  const parseFreeHours = (cargoType) => {
    const fp = freePeriods.find(p => p.type === cargoType);
    if (!fp || fp.free_period === 'None' || fp.free_period === 'No need') return 0;
    const match = fp.free_period.match(/\d+/);
    return match ? parseInt(match[0]) : 0;
  };

  // ✅ دالة حساب التخزين بناءً على جدول storage (تم إصلاح undefined)
  const calculateStorageFee = (bol, departureDate) => {
    const startDate = bol.storageStartDate || bol.storage_start_date || manifest.general.storageStartDate;
    const endDate = departureDate || bol.storageEndDate || bol.storage_end_date || manifest.general.storageEndDate;
    if (!startDate || !endDate) return { fee: 0, days: 0 };

    // 1. حساب إجمالي الأيام (شاملة)
    const totalDays = calculateDays(startDate, endDate, bol.day);
    if (totalDays <= 0) return { fee: 0, days: 0 };

    // 2. تحويل الأيام إلى ساعات وطرح الساعات المجانية
    const freeHours = parseFreeHours(bol.cargoType || "GCR");
    const totalHours = totalDays * 24;
    const billableHours = Math.max(0, totalHours - freeHours);

    // 3. تحويل الساعات المتبقية إلى أيام شحن (أي جزء من اليوم = يوم كامل)
    const billableDays = Math.ceil(billableHours / 24);

    return { fee: calculateStorageAmount(bol, billableDays), days: billableDays };
  };

  const calculateStorageAmount = (bol, days) => {
    if (days <= 0) return 0;

    const weight = Number(bol.grossMass || 0);
    const cargoType = bol.cargoType || "GCR";
    const rate = storageItems.find(s => 
      s.type === cargoType && 
      weight >= (s.rangeFrom || 0) && 
      (s.rangeTo === null || weight <= s.rangeTo)
    );
    if (!rate || rate.price === 'No need') return 0;
    return rate.isFixed ? Number(rate.price) * days : Number(rate.price) * weight * days;
  };

  // ✅ دالة مساعدة لعرض سعر التخزين لكل بوليصة
  const getStorageRateInfo = (bol) => {
    const weight = Number(bol.grossMass || 0);
    const cargoType = bol.cargoType || "GCR";
    const rate = storageItems.find(s => 
      s.type === cargoType && 
      weight >= (s.rangeFrom || 0) && 
      (s.rangeTo === null || weight <= s.rangeTo)
    );
    if (!rate) return "—";
    if (rate.price === 'No need') return 'No need';
    return `${rate.price} ${rate.isFixed ? '(Fixed)' : '/ kg'}`;
  };

  const findServiceLabel = (s) => {
    if (!s) return "";
    const base = s.name && String(s.name).trim()
      ? String(s.name).trim()
      : `${s.type || "Unknown"} ${s.classification ? `(${s.classification})` : ""}`.trim();
    const priceNum = Number(s.price);
    return Number.isFinite(priceNum)
      ? `${base} · ${priceNum.toFixed(2)} USD`
      : base;
  };

  useEffect(() => {
    const fetch = async () => {
      try {
        const [s, p, b, st, ct, fp] = await Promise.all([
          api.get("/services"),
          api.get("/packing"),
          api.get("/build-up"),
          api.get("/storage"),
          api.get("/cargo-types"),
          api.get("/free-periods"),
        ]);
        
        const mappedServices = Array.isArray(s) ? s.map(svc => {
          const flat = {};
          if (svc.prices) {
            Object.keys(svc.prices).forEach(k => flat[k.toLowerCase()] = svc.prices[k]);
          }
          return { ...svc, ...flat };
        }) : [];

        setServicesList(mappedServices);
        setPackingItems(Array.isArray(p) ? p : []);
        setBuildUpItems(Array.isArray(b) ? b : []);
        setCargoTypes(Array.isArray(ct) ? ct : []);
        setFreePeriods(Array.isArray(fp) ? fp : []);
        setStorageItems(Array.isArray(st) ? st.map(item => ({
          ...item,
          rangeFrom: item.range_from ?? item.rangeFrom ?? null,
          rangeTo: item.range_to ?? item.rangeTo ?? null,
          isFixed: item.is_fixed ?? item.isFixed ?? false
        })) : []);
      } catch (err) {
        console.error("Error loading services:", err);
        setServicesList([]);
        setPackingItems([]);
        setBuildUpItems([]);
        setCargoTypes([]);
        setFreePeriods([]);
        setStorageItems([]);
      }
    };
    fetch();
  }, []);

  useEffect(() => {
    const allBols = flattenBols(manifest.bols);
    const totalServiceCost = allBols.reduce((sum, bol) => {
      return sum + getBolTotal(bol);
    }, 0);

    // حساب إجمالي التخزين لكل البوالص بناءً على تاريخ المغادرة
    const departureDate = manifest.general.dateOfArrival;
    const totalStorageCharge = allBols.reduce((sum, bol) => {
      return sum + calculateStorageFee(bol, departureDate).fee;
    }, 0);

    const totalInvoice = totalServiceCost + totalStorageCharge;

    setComputedTotals({
      storageDays: "—",
      totalServiceCost: totalServiceCost.toFixed(2),
      totalStorageCharge: totalStorageCharge.toFixed(2),
      totalInvoice: totalInvoice.toFixed(2),
    });
  }, [manifest.bols, manifest.general.dateOfArrival, storageItems, servicesList, packingItems, buildUpItems]);

  useEffect(() => {
    if (!manifest.bols || manifest.bols.length === 0) return;

    setManifest((prev) => {
      const hydratedBols = prev.bols.map(hydrateBolItems);
      if (JSON.stringify(hydratedBols) === JSON.stringify(prev.bols)) return prev;
      return { ...prev, bols: hydratedBols };
    });
  }, [manifest.bols, servicesList, packingItems, buildUpItems]);

useEffect(() => {
  if (!voyageDraft.voyageNumber?.trim()) return;

  // لا تحفظ إذا كل الحقول الأخرى فاضية
  if (
    !voyageDraft.ownerOperator?.trim() &&
    !voyageDraft.customsOfficeCode?.trim()
  ) {
    return;
  }

  const t = setTimeout(async () => {
    try {
      await api.post("/voyages", {
        voyageNumber: voyageDraft.voyageNumber.trim(),
        ownerOperator: voyageDraft.ownerOperator?.trim() || null,
        customsOfficeCode: voyageDraft.customsOfficeCode?.trim() || null
      });
    } catch (e) {
      console.error("Auto save voyage failed", e);
    }
  }, 600);

  return () => clearTimeout(t);
}, [voyageDraft]);


useEffect(() => {
  if (!showOwnerDropdown) return;

  const t = setTimeout(async () => {
    try {
      const q = (ownerQuery || "").trim();
      const url = q ? `/owners?q=${encodeURIComponent(q)}` : `/owners`;
      const rows = await api.get(url);
      setOwnerOptions(rows || []);
      setShowOwnerDropdown(true);
    } catch (err) {
      console.error("Error fetching owners:", err);
      setOwnerOptions([]);
    }
  }, 250);

  return () => clearTimeout(t);
}, [ownerQuery, showOwnerDropdown]);

  const permissions = readPerms();
  const canSearchManifest = has(permissions, "MANIFEST_SEARCH") || has(permissions, "MANIFEST_EDIT");
  const canCreateManifest = has(permissions, "MANIFEST_CREATE") || has(permissions, "MANIFEST_ENTRY");
  const canEditManifest = has(permissions, "MANIFEST_EDIT") || has(permissions, "SERVICES_EDIT");

  const loadManifestList = async (force = false) => {
    if (!canSearchManifest) return;
    if (!force && manifestList.length > 0) return;
    try {
      const res = await api.get("/manifests/list");
      setManifestList(res);
      setFilteredManifests(res);
    } catch (err) {
      console.error(err);
    }
  };

  const handleManifestInputChange = (value) => {
    setManifestQuery(value);
    const v = value.trim().toLowerCase();
    if (!v) {
      setFilteredManifests(manifestList);
      return;
    }
    const filtered = manifestList.filter((m) => {
      const idStr = String(m.id || "").toLowerCase();
      const voyage = String(m.voyage_number || "").toLowerCase();
      const arrival = String(m.date_of_arrival || "").toLowerCase();
      return idStr.includes(v) || voyage.includes(v) || arrival.includes(v);
    });
    setFilteredManifests(filtered);
  };

  // ✅ Function to add a paid BOL to the manifest
  // ✅ Function to add a paid BOL to the manifest
  const importPaidBol = async (bol) => {
    // ❌ لا داعي لاستدعاء API منفصل - الـ Backend يتولى ذلك عند الحفظ
    
    setManifest((prev) => {
          let master = mapBolRowToUi(bol);

      // 1. إذا كان الهيدر فارغاً، نأخذ القيم من البوليصة المحملة
      const headerUpdate = {};
      if (!prev.general.dateOfDeparture && master.storageStartDate) headerUpdate.dateOfDeparture = master.storageStartDate;
      if (!prev.general.dateOfArrival && master.storageEndDate) headerUpdate.dateOfArrival = master.storageEndDate;

      // 2. ضمان تطابق تواريخ البوليصة (الماستر والهاوس) مع الهيدر الحالي أو المحدث
      const finalDeparture = prev.general.dateOfDeparture || headerUpdate.dateOfDeparture || master.storageStartDate;
      const finalArrival = prev.general.dateOfArrival || headerUpdate.dateOfArrival || master.storageEndDate;

      master.storageStartDate = finalDeparture;
      master.storageEndDate = finalArrival;
      
      // ✅ أهم: عند استيراد بوليصة من جدول bols، يجب تعيين id إلى null لأن هذا ليس معرف من manifest_bols
      // هذا يضمن أن السيرفر سيتعامل معها كبوليصة جديدة للإدراج وليس للتحديث
      master.id = null;
      master.tempId = makeTempId();
      
      // جلب الهاوسات التي تم تجميعها في السيرفر
      if (Array.isArray(bol.homes)) {
        master.homes = bol.homes.map(h => {
          const hh = mapBolRowToUi(h);
          hh.storageStartDate = finalDeparture;
          hh.storageEndDate = finalArrival;
          return hh;
        });
        master.bolTypeCode = "741"; // تأكيد أنه ماستر
      }

      const newBols = [...prev.bols, master];
      const totals = computeTotalsFromBols(newBols);
      return { ...prev, bols: newBols, general: { ...prev.general, ...totals, ...headerUpdate } };
    });
    setShowPaidBolsDropdown(false);
    
    // ✅ إخفاء البوليصة فوراً من القائمة المحلية بدون انتظار الـ API
    setPaidBols(prev => prev.filter(b => b.bol_reference !== bol.bol_reference));
  };

  // ✅ Flight takeoff function
const handleTakeOff = async () => {
  if (!selectedManifestId) return;
  if (!window.confirm("Do you want to confirm flight takeoff?...")) return;
  
  try {
    const res = await api.post(`/manifests/${selectedManifestId}/takeoff`);
    
    if (res.invoices && Array.isArray(res.invoices)) {
      res.invoices.forEach(inv => {
        const masterBolOrig = manifest.bols.find(b => b.id === inv.masterId);
        if (masterBolOrig) {
          const printData = { ...masterBolOrig };

          // ✅ استخدام calculateStorageFee الصحيحة (تستخدم storageItems + freePeriods)
          const departureDate = manifest.general.dateOfArrival;
          const masterStorage = calculateStorageFee(printData, departureDate);
          
          // ✅ تمرير القيم المحسوبة بشكل صريح بـ snake_case
          printData.storage_fee = masterStorage.fee;
          printData.billable_days = masterStorage.days;
          printData.storage_start_date = masterBolOrig.storageStartDate || masterBolOrig.storage_start_date;
          printData.storage_end_date = masterBolOrig.storageEndDate || masterBolOrig.storage_end_date || departureDate;

          if (Array.isArray(printData.homes)) {
            printData.homes = printData.homes.map(h => {
              const s = calculateStorageFee(h, departureDate);
              return { 
                ...h, 
                storage_fee: s.fee,
                billable_days: s.days,
                storage_start_date: h.storageStartDate || h.storage_start_date,
                storage_end_date: h.storageEndDate || h.storage_end_date || departureDate,
                bol_reference: h.bolReference,
                number_of_packages: h.numberOfPackages,
                gross_mass: h.grossMass,
                goods_description: h.goodsDescription,
              };
            });
          }

          // ✅ تجاهل تكلفة الخدمات في فاتورة التخزين المطبوعة عند الإقلاع
          const totalService = 0; 
          const totalStorage = masterStorage.fee + 
            (printData.homes?.reduce((sum, h) => sum + Number(h.storage_fee), 0) || 0);

          // ✅ إضافة service_name و packing_type للماستر
          printData.service_name = masterBolOrig.services?.[0]?.name || '';
          printData.packing_type = masterBolOrig.packingItems?.[0]?.type || '';
          Object.assign(printData, {
            ...manifest.general,
            // ✅ snake_case للحقول التي يبحث عنها getVal في الـ PDF
            bol_reference: printData.bolReference,
            number_of_packages: printData.numberOfPackages,
            gross_mass: printData.grossMass,
            goods_description: printData.goodsDescription,
            flight_no: manifest.general.flightNo,
            d_no: inv.d_no,
            storage_end_date: departureDate,
            fees: {
              service: totalService,
              storage: totalStorage,
              total: totalService + totalStorage,
            },
          });

          generateBolInvoicePdf({ bol: printData });
        }
      });
    }
      alert("Flight takeoff registered successfully ✅");
      // تحديث البيانات لعرض الحالة الجديدة
      handleSelectManifest({ id: selectedManifestId });
    } catch (err) {
      console.error(err);
      alert("Error registering takeoff");
    }
  };

  // // ✅ دالة حساب التخزين بناءً على جدول storage
  // const calculateStorageFee = (bol, departureDate) => {
  //   const startDate = bol.storageStartDate; // تاريخ الإيصال
  //   const endDate = departureDate; // تاريخ مغادرة الرحلة
  //   const days = calculateDays(startDate, endDate, bol.day);
    
  //   if (days <= 0) return 0;

  //   const weight = Number(bol.grossMass || 0);
  //   const cargoType = bol.cargoType || "GCR";

  //   // البحث في جدول التخزين عن الفئة المناسبة للوزن
  //   const rate = storageItems.find(s => 
  //     s.type === cargoType && 
  //     weight >= (s.rangeFrom || 0) && 
  //     (s.rangeTo === null || weight <= s.rangeTo)
  //   );

  //   if (!rate || rate.price === 'No need') return 0;

  //   let total = 0;
  //   if (rate.isFixed) {
  //     total = Number(rate.price) * days;
  //   } else {
  //     total = Number(rate.price) * weight * days;
  //   }
  //   return total;
  // };

    const getItemsFromIds = (ids, itemList) => {
    if (ids == null) return [];
    const parsedIds = parseIdArray(ids);
    return parsedIds.map(id => itemList.find(item => item.id == id)).filter(Boolean);
  };

  const hydrateBolItems = (b) => ({
    ...b,
    services: getItemsFromIds(b.service_ids ?? b.service_id ?? b.serviceId, servicesList),
    packingItems: getItemsFromIds(b.packing_item_ids ?? b.packing_item_id ?? b.packingItemId, packingItems),
    buildUpItems: getItemsFromIds(b.build_up_item_ids ?? b.build_up_item_id ?? b.buildUpItemId, buildUpItems),
    homes: Array.isArray(b.homes) ? b.homes.map(h => hydrateBolItems(h)) : [],
  });

  const mapBolRowToUi = (b) => ({
    id: b.id,
    parent_bol_id: b.parent_bol_id || b.parentBolId || null,
    tempId: b.id ? null : makeTempId(),
    isLocked: Number(b.is_locked) === 1,
    day: Number(b.day || b.day === true) === 1, // Ensure boolean for checkbox
    bolReference: b.bol_reference || b.bolReference || "",
    cargoType: b.cargo_type || b.cargoType || "",
    lineNumber: b.line_number || b.lineNumber || "",
    bolNature: b.bol_nature || b.bolNature || "23", // Default to "23" if not provided
    bolTypeCode: b.bol_type_code || b.bolTypeCode || "",
    placeOfLoadingCode: b.place_of_loading_code || b.placeOfLoadingCode || "",
    placeOfUnloadingCode: b.place_of_unloading_code || b.placeOfUnloadingCode || "",
    exporterName: b.exporter_name || b.exporterName || "",
    exporterAddress: b.exporter_address || b.exporterAddress || "",
    consigneeName: b.consignee_name || b.consigneeName || "",
    consigneeAddress: b.consignee_address || b.consigneeAddress || "",
    notifyName: b.notify_name || b.notifyName || "",
    notifyAddress: b.notify_address || b.notifyAddress || "",
    numberOfPackages: (b.number_of_packages ?? b.numberOfPackages)?.toString() || "",
    packageTypeCode: b.package_type_code || b.packageTypeCode || "",
    grossMass: (b.gross_mass ?? b.grossMass)?.toString() || "",
    shippingMarks: b.shipping_marks || b.shippingMarks || "",
    goodsDescription: b.goods_description || b.goodsDescription || "",
    numOfCtnForThisBol: (b.num_of_ctn_for_this_bol ?? b.numOfCtnForThisBol)?.toString() || "",
    storageStartDate: (b.storage_start_date || b.storageStartDate) ? toDateInput(b.storage_start_date || b.storageStartDate) : "",
    storageEndDate: (b.storage_end_date || b.storageEndDate) ? toDateInput(b.storage_end_date || b.storageEndDate) : "",
    serviceId: (parseIdArray(b.service_ids)[0] ?? b.service_id ?? b.serviceId), // Keep single ID for compatibility
    services: getItemsFromIds(b.service_ids ?? b.service_id ?? b.serviceId, servicesList),
    packingItemId: (parseIdArray(b.packing_item_ids)[0] ?? b.packing_item_id ?? b.packingItemId), // Keep single ID
    packingItems: getItemsFromIds(b.packing_item_ids ?? b.packing_item_id ?? b.packingItemId, packingItems),
    buildUpItemId: (parseIdArray(b.build_up_item_ids)[0] ?? b.build_up_item_id ?? b.buildUpItemId), // Keep single ID
    buildUpItems: getItemsFromIds(b.build_up_item_ids ?? b.build_up_item_id ?? b.buildUpItemId, buildUpItems),
    homes: Array.isArray(b.homes) ? b.homes.map(h => mapBolRowToUi(h)) : [], // Recursive call
  });

  const handleSelectManifest = async (m) => {
    try {
      setLoading(true);
      setShowManifestDropdown(false);
      setManifestQuery(String(m.id));
      setSelectedManifestId(m.id);

      // ✅ تم تعديل المسار ليتوافق مع السيرفر (إضافة /full)
      const res = await api.get(`/manifests/${m.id}/full`);
      const { header, bols } = res;

      const general = {
        ownerOperator: header.owner_operator || "",
        registration: header.registration || "",
        flightNo: header.flight_no || "",
        customsOfficeCode: header.customs_office_code || "",
        declaredTotalBols: header.declared_total_bols != null ? String(header.declared_total_bols) : "",
        voyageNumber: header.voyage_number || "",
        dateOfDeparture: toDateInput(header.date_of_departure),
        dateOfArrival: toDateInput(header.date_of_arrival),
        consigneeName: header.consignee_name || "",
        consigneeAddress: header.consignee_address || "",
        totalBols: (header.total_bols ?? (bols || []).length)?.toString() || "",
        totalPackages: (header.total_packages ?? "")?.toString() || "",
        totalContainers: (header.total_containers ?? "")?.toString() || "",
        totalGrossMass: (header.total_gross_mass ?? "")?.toString() || "",
        modeOfTransportCode: header.mode_of_transport_code || "",
        transporterNationality: header.transporter_nationality || "",
        placeOfDepartureCode: header.place_of_departure_code || "",
        placeOfDestinationCode: header.place_of_destination_code || "",
        carrierCode: header.carrier_code || "",
        carrierName: header.carrier_name || "",
        carrierAddress: header.carrier_address || "",
        shippingAgentCode: header.shipping_agent_code || "",
        shippingAgentName: header.shipping_agent_name || "",
        identityOfTransporter: header.identity_of_transporter || "",
        storageStartDate: header.storage_start_date ? toDateInput(header.storage_start_date) : "",
        storageEndDate: header.storage_end_date ? toDateInput(header.storage_end_date) : "",
        storageDays: header.storage_days != null ? String(header.storage_days) : "0",
        storageRate: header.storage_rate != null ? String(header.storage_rate) : "0",
        totalServiceCost: header.total_service_cost != null ? String(header.total_service_cost) : "0",
        totalStorageCharge: header.total_storage_charge != null ? String(header.total_storage_charge) : "0",
        totalInvoice: header.total_invoice != null ? String(header.total_invoice) : "0",
      };

      const mappedMasters = (bols || []).map((masterRow) => mapBolRowToUi(masterRow));

      const totals = computeTotalsFromBols(mappedMasters);
      setManifest({ general: { ...general, ...totals }, bols: mappedMasters });
      setMode("manual");
      setMessage({ text: "Manifest loaded successfully ✅", type: "success" });
    } catch (err) {
      console.error(err);
      setMessage({ text: err?.message || "Error loading manifest details", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const parseAwmdsXml = (xmlText) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlText, "application/xml");
    const genId = doc.querySelector("General_segment_id");
    const totalsSeg = doc.querySelector("Totals_segment");
    const transInfo = doc.querySelector("Transport_information");
    const loadPlace = doc.querySelector("Load_unload_place");
    const consigneeHeader = genId?.querySelector("Consignee");

    const general = {
      customsOfficeCode: getText(genId, "Customs_office_code"),
      voyageNumber: getText(genId, "Voyage_number"),
      dateOfDeparture: toDateInput(getText(genId, "Date_of_departure")),
      dateOfArrival: toDateInput(getText(genId, "Date_of_departure") || getText(genId, "Date_of_arrival")),
      consigneeName: getText(consigneeHeader, "Consignee_name"),
      consigneeAddress: getText(consigneeHeader, "Consignee_address"),
      carrierName: getText(transInfo?.querySelector("Carrier"), "Carrier_name"),
      carrierAddress: getText(transInfo?.querySelector("Carrier"), "Carrier_address"),
      shippingAgentCode: getText(transInfo?.querySelector("Shipping_Agent"), "Shipping_Agent_code"),
      shippingAgentName: getText(transInfo?.querySelector("Shipping_Agent"), "Shipping_Agent_name"),
      modeOfTransportCode: getText(transInfo, "Mode_of_transport_code"),
      identityOfTransporter: getText(transInfo, "Identity_of_transporter"),
      transporterNationality: getText(transInfo, "Nationality_of_transporter_code"),
      placeOfDepartureCode: getText(loadPlace, "Place_of_departure_code"),
      placeOfDestinationCode: getText(loadPlace, "Place_of_destination_code"),
      storageStartDate: toDateInput(getText(genId, "Storage_start_date")),
      storageEndDate: toDateInput(getText(genId, "Storage_end_date")),
      storageDays: getText(genId, "Storage_days"),
      storageRate: getText(genId, "Storage_rate"),
      totalServiceCost: getText(genId, "Total_service_cost"),
      totalStorageCharge: getText(genId, "Total_storage_charge"),
      totalInvoice: getText(genId, "Total_invoice"),
    };

    const bolNodes = Array.from(doc.querySelectorAll("Bol_segment"));
    const masters = [];
    const houses = [];

    bolNodes.forEach((bolNode) => {
      const bolId = bolNode.querySelector("Bol_id");
      const bolTypeCode = getText(bolId, "Bol_type_code");
      const bolObj = {
        id: null,
        tempId: makeTempId(),
        isLocked: false,
        bolReference: getText(bolId, "Bol_reference"),
        lineNumber: getText(bolId, "Line_number"),
        bolNature: getText(bolId, "Bol_nature"),
        masterBolRefNumber: getText(bolId, "Master_bol_ref_number"),
        bolTypeCode,
        placeOfLoadingCode: getText(bolNode.querySelector("Load_unload_place"), "Place_of_loading_code"),
        placeOfUnloadingCode: getText(bolNode.querySelector("Load_unload_place"), "Place_of_unloading_code"),
        exporterName: getText(bolNode.querySelector("Exporter"), "Exporter_name"),
        exporterAddress: getText(bolNode.querySelector("Exporter"), "Exporter_address"),
        consigneeName: getText(bolNode.querySelector("Consignee"), "Consignee_name"),
        consigneeAddress: getText(bolNode.querySelector("Consignee"), "Consignee_address"),
        notifyName: getText(bolNode.querySelector("Notify"), "Notify_name"),
        notifyAddress: getText(bolNode.querySelector("Notify"), "Notify_address"),
        numberOfPackages: getText(bolNode.querySelector("Goods_segment"), "Number_of_packages"),
        packageTypeCode: getText(bolNode.querySelector("Goods_segment"), "Package_type_code"),
        grossMass: getText(bolNode.querySelector("Goods_segment"), "Gross_mass"),
        shippingMarks: getText(bolNode.querySelector("Goods_segment"), "Shipping_marks"),
        goodsDescription: getText(bolNode.querySelector("Goods_segment"), "Goods_description"),
        numOfCtnForThisBol: getText(bolNode.querySelector("Goods_segment"), "Num_of_ctn_for_this_bol"),
        storageStartDate: (() => {
          const value = getText(bolNode, "Storage_start_date") || general.storageStartDate;
          return value ? toDateInput(value) : "";
        })(),
        storageEndDate: (() => {
          const value = getText(bolNode, "Storage_end_date") || general.storageEndDate;
          return value ? toDateInput(value) : "";
        })(),
        serviceId: getText(bolNode.querySelector("Service_segment"), "Service_id"),
        packingItemId: null,
        buildUpItemId: null,
        homes: [],
      };

      if (bolTypeCode === "H" || bolObj.masterBolRefNumber) {
        houses.push(bolObj);
      } else {
        masters.push(bolObj);
      }
    });

    houses.forEach((house) => {
      const masterRef = house.masterBolRefNumber || house.bolNature;
      const master = masters.find((m) => m.bolReference === masterRef);
      if (master) {
        master.homes.push(house);
      } else {
        masters.push(house);
      }
    });

    masters.forEach((master) => {
      if (!master.storageStartDate && general.storageStartDate) {
        master.storageStartDate = general.storageStartDate;
      }
      if (!master.storageEndDate && general.storageEndDate) {
        master.storageEndDate = general.storageEndDate;
      }
      if (Array.isArray(master.homes)) {
        master.homes = master.homes.map((h) => ({
          ...h,
          storageStartDate: h.storageStartDate || master.storageStartDate || general.storageStartDate || "",
          storageEndDate: h.storageEndDate || master.storageEndDate || general.storageEndDate || "",
        }));
      }
    });

    const totals = computeTotalsFromBols(masters);
    return { general: { ...general, ...totals }, bols: masters };
  };

  const handleFileChange = (e) => {
    setXmlFile(e.target.files[0] || null);
    setMessage({ text: "", type: "" });
  };

  const handleLoadFromXml = () => {
    if (!xmlFile) {
      setMessage({ text: "Please select an XML file first", type: "error" });
      return;
    }
    resetForm();
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let xmlText = e.target.result;
        xmlText = xmlText.replaceAll("SDPZU", "SDPZU");
        const parsed = parseAwmdsXml(xmlText);
        setSelectedManifestId(null);
        setManifestQuery("");
        setShowManifestDropdown(false);
        setManifest(parsed);
        setMode("manual");
        setMessage({ text: "XML data loaded successfully ✅", type: "success" });
      } catch (err) {
        console.error(err);
        setMessage({ text: "Error while reading XML file", type: "error" });
      }
    };
    reader.readAsText(xmlFile);
  };

  const handleGeneralChange = (field, value) => {
   setManifest((prev) => {
      const newGeneral = { ...prev.general, [field]: value };
      let newBols = [...prev.bols];

      // عند تغيير تاريخ المغادرة أو الوصول في الهيدر، نقوم بتحديث كل البوالص التابعة للمانيفست
      if (field === "dateOfDeparture" || field === "dateOfArrival") {
        newBols = newBols.map((m) => {
          const updatedMaster = { ...m };
          if (field === "dateOfDeparture") updatedMaster.storageStartDate = value;
          if (field === "dateOfArrival") updatedMaster.storageEndDate = value;

          if (Array.isArray(updatedMaster.homes)) {
            updatedMaster.homes = updatedMaster.homes.map((h) => ({
              ...h,
              storageStartDate: field === "dateOfDeparture" ? value : h.storageStartDate,
              storageEndDate: field === "dateOfArrival" ? value : h.storageEndDate,
            }));
          }
          return updatedMaster;
        });
      }
      return { ...prev, general: newGeneral, bols: newBols };
    });
  };

  const handlePlaceOfDepartureChange = (value) => {
    if (value.length > 5) {
      setMessage({ text: "Warning: Point of loading cannot exceed 5 characters", type: "error" });
      return;
    }
    handleGeneralChange("placeOfDepartureCode", value);

    const trip = PREDEFINED_TRIPS.find(t => t.pointOfLoading.toUpperCase() === value.toUpperCase());

    if (trip) {
      handleGeneralChange("voyageNumber", trip.voyageNumber);
      handleGeneralChange("flightNo", trip.voyageNumber);
    }
  };

const handleBolChange = (masterIndex, field, value, homeIndex = null) => {
  setManifest(prev => {
    const masters = [...prev.bols];
    const master = masters[masterIndex];
    if (!master) return prev;

    const isDistributable = field === "grossMass" || field === "numberOfPackages";

    // ======================
    // MASTER → HOUSES
    // ======================
    if (homeIndex === null) {
      if (master.isLocked) return prev;

      const updatedMaster = { ...master, [field]: value };

      if (isDistributable && Array.isArray(master.homes) && master.homes.length > 0) {
        const perHouse = Number(value || 0) / master.homes.length;

        updatedMaster.homes = master.homes.map(h => ({
          ...h,
          [field]: Number.isFinite(perHouse)
            ? perHouse.toFixed(2)
            : ""
        }));
      }

      masters[masterIndex] = updatedMaster;
    }

    // ======================
    // HOUSES → MASTER
    // ======================
    if (homeIndex !== null) {
      const homes = [...(master.homes || [])];
      const home = homes[homeIndex];
      if (!home || home.isLocked) return prev;

      homes[homeIndex] = { ...home, [field]: value };

      let updatedMaster = { ...master, homes };

      if (isDistributable) {
        const sum = homes.reduce(
          (acc, h) => acc + Number(h[field] || 0),
          0
        );
        updatedMaster[field] = sum.toFixed(2);
      }

      masters[masterIndex] = updatedMaster;
    }

    // ======================
    // AWB TYPE CODE (AUTO)
    // ======================
    masters.forEach(m => {
      if (!m.homes || m.homes.length === 0) {
        m.bolTypeCode = "740";
      } else {
        m.bolTypeCode = "741";
        m.homes.forEach(h => {
          h.bolTypeCode = "740";
        });
      }
    });

    const totals = computeTotalsFromBols(masters);
    return {
      ...prev,
      bols: masters,
      general: { ...prev.general, ...totals }
    };
  });
};


  const handleAddBol = () => {
    setManifest((prev) => {
      const newMaster = {
        id: null,
        tempId: makeTempId(),
        isLocked: false,
        bolReference: "",
        lineNumber: "",
        bolNature: "23",
        bolTypeCode: "740",
        packageTypeCode: "PK",
        placeOfLoadingCode: "",
        placeOfUnloadingCode: "",
        exporterName: "",
        exporterAddress: "",
        consigneeName: "",
        consigneeAddress: "",
        notifyName: "PORTSUDAN SUDAN",
        notifyAddress: "PORTSUDAN SUDAN",
        numberOfPackages: "",
        grossMass: "",
        shippingMarks: "A",
        goodsDescription: "",
        numOfCtnForThisBol: "0", // مخفي
        day: false,
        storageStartDate: prev.general.storageStartDate || "",
        storageEndDate: prev.general.storageEndDate || "",
        serviceId: null,
        packingItemId: null,
        buildUpItemId: null,
        homes: [],
      };
      const newBols = [...prev.bols, newMaster];
      const totals = computeTotalsFromBols(newBols);
      return { ...prev, bols: newBols, general: { ...prev.general, ...totals } };
    });
  };

  const handleAddHome = (masterIndex) => {
    setManifest((prev) => {
      const masters = [...prev.bols];
      const master = masters[masterIndex];
      if (!master || master.isLocked) return prev;
      const homes = Array.isArray(master.homes) ? [...master.homes] : [];
      const newHome = {
        id: null,
        tempId: makeTempId(),
        isLocked: false,
        bolReference: "",
        lineNumber: "",
        bolNature: "23",
        bolTypeCode: "740",
        packageTypeCode: "PK",
        placeOfLoadingCode: "",
        placeOfUnloadingCode: "",
        exporterName: "",
        exporterAddress: "",
        consigneeName: "",
        consigneeAddress: "",
        notifyName: "PORTSUDAN SUDAN",
        notifyAddress: "PORTSUDAN SUDAN",
        numberOfPackages: "",
        grossMass: "",
        shippingMarks: "A",
        goodsDescription: "",
        numOfCtnForThisBol: "0", // مخفي
        day: false,
        storageStartDate: master.storageStartDate || prev.general.storageStartDate || "",
        storageEndDate: master.storageEndDate || prev.general.storageEndDate || "",
        serviceId: null,
        packingItemId: null,
        buildUpItemId: null,
      };
      masters[masterIndex] = { ...master, homes: [...homes, newHome], bolTypeCode: "741" };
      const totals = computeTotalsFromBols(masters);
      return { ...prev, bols: masters, general: { ...prev.general, ...totals } };
    });
  };

  // ✅ Delete Master BOL
  const handleDeleteBol = (index) => {
    if (!window.confirm("Are you sure you want to delete this Master AWB?")) return;
    setManifest((prev) => {
      const newBols = [...prev.bols];
      newBols.splice(index, 1);
      const totals = computeTotalsFromBols(newBols);
      return { ...prev, bols: newBols, general: { ...prev.general, ...totals } };
    });
  };

  // ✅ Delete House BOL
  const handleDeleteHome = (masterIndex, homeIndex) => {
    if (!window.confirm("Are you sure you want to delete this House AWB?")) return;
    setManifest((prev) => {
      const masters = [...prev.bols];
      const master = { ...masters[masterIndex] };
      const homes = [...(master.homes || [])];
      homes.splice(homeIndex, 1);
      master.homes = homes;
      if (homes.length === 0) master.bolTypeCode = "740"; // Revert to direct AWB if no houses
      masters[masterIndex] = master;
      const totals = computeTotalsFromBols(masters);
      return { ...prev, bols: masters, general: { ...prev.general, ...totals } };
    });
  };

  const validateManifest = () => {
    const g = manifest.general;
    // Removed storageStartDate and storageEndDate from general validation as they are now per BOL
    if (!g.customsOfficeCode || !g.voyageNumber || !g.dateOfArrival) {
      return "Warning: Please fill all required manifest header fields (marked in red).";
    }
    const all = flattenBols(manifest.bols);
    if (all.length === 0) return "Warning: At least one BOL must be added to the manifest.";
    for (let i = 0; i < all.length; i++) {
      const b = all[i];
      if (
        !b.cargoType || 
        !b.bolReference || 
        !b.bolNature || 
        !b.bolTypeCode || 
        !b.notifyName ||
        !b.notifyAddress ||
        !b.numberOfPackages ||
        !b.packageTypeCode ||
        !b.grossMass ||
        !b.shippingMarks ||
        !b.goodsDescription ||
        (!b.serviceId && !b.packingItemId && !b.buildUpItemId)
      ) {
        return `Warning: Please fill required fields for AWB #${i + 1} (including Cargo Type and Services).`;
      }
    }
    return null;
  };

  const handleExportXml = () => {
    const xmlText = manifestToAwmdsXml(manifest);
    const fileName = `MANIFEST_${manifest.general.voyageNumber || "NEW"}_${manifest.general.dateOfArrival || ""}.xml`;
    downloadXmlFile(xmlText, fileName);
  };

  const handleSave = async (silent = false) => {
    if (selectedManifestId && !canEditManifest) {
      setMessage({ text: "ليس لديك صلاحية تعديل Manifest", type: "error" });
      return false;
    }
    if (!selectedManifestId && !canCreateManifest) {
      setMessage({ text: "ليس لديك صلاحية إنشاء Manifest", type: "error" });
      return false;
    }

    // ✅ Prevent duplicate BOL reference
    const allBols = flattenBols(manifest.bols);
    const refs = allBols.map(b => String(b.bolReference).trim().toUpperCase());
    if (new Set(refs).size !== refs.length) {
      setMessage({
        text: "Warning: BOL reference (AWB Reference) is duplicated. Please ensure there are no duplicates.",
        type: "error",
      });
      return false;
    }

    // ===
    // التحقق من البيانات
    // ===


    const errMsg = validateManifest();
    if (errMsg) {
      setShowErrors(true);
      setMessage({ text: errMsg, type: "error" });
      return false;
    }
    setShowErrors(false);
    try {
      setLoading(true);
      setMessage({ text: "", type: "" });
      const editableMasters = (manifest.bols || [])
        .map((m) => {
          // If the master is hidden or has the success icon, it's already been saved or is processing. Skip it.
          if (m.hidden || m.showSuccessIcon) return null;
          if (m.isLocked && (!m.homes || m.homes.length === 0)) return null;

          const homes = Array.isArray(m.homes) ? m.homes : [];
          // Also filter out hidden or success-marked homes
          const editableHomes = homes.filter((h) => (!h.isLocked || !h.id) && !h.hidden && !h.showSuccessIcon);

          if (!(!m.isLocked || !m.id) && editableHomes.length === 0) return null;

          const cleanedMaster = { ...m, homes: editableHomes };
          delete cleanedMaster.tempId;
          cleanedMaster.homes = cleanedMaster.homes.map((h) => {
            const hh = { ...h };
            delete hh.tempId;
            return hh;
          });
          return cleanedMaster;
        })
        .filter(Boolean);

      const payload = { general: manifest.general, bols: editableMasters };
      let savedManifestId = selectedManifestId;
      if (selectedManifestId) {
        await api.put(`/manifests/${selectedManifestId}`, payload);
      } else {
        const createRes = await api.post("/manifest", payload);
        savedManifestId = createRes.headerId || createRes.id || savedManifestId;
        if (savedManifestId) {
          setSelectedManifestId(savedManifestId);
        }
      }

      if (savedManifestId) {
        await handleSelectManifest({ id: savedManifestId });
      }

      // Save owner profile on successful manifest save
      if (manifest.general.ownerOperator?.trim()) {
        if (manifest.general.flightNo?.trim() || manifest.general.customsOfficeCode?.trim() || manifest.general.voyageNumber?.trim()) {
          try {
            await api.post("/owners", {
              ownerOperator: manifest.general.ownerOperator.trim(),
              flightNo: manifest.general.flightNo?.trim() || null,
              customsOfficeCode: manifest.general.customsOfficeCode?.trim() || null,
              voyageNumber: manifest.general.voyageNumber?.trim() || null,
            });
          } catch (ownerErr) {
            console.error("Could not save owner profile:", ownerErr);
            // Non-critical error, just log it.
          }
        }
      }

      // ✅ Save Shippers & Consignees locally (sqflit style)
      manifest.bols.forEach(b => {
        saveLocalPair("shippers", b.exporterName, b.exporterAddress);
        saveLocalPair("consignees", b.consigneeName, b.consigneeAddress);
        saveLocalValue("goods_descriptions", b.goodsDescription);
        (b.homes || []).forEach(h => {
          saveLocalPair("shippers", h.exporterName, h.exporterAddress);
          saveLocalPair("consignees", h.consigneeName, h.consigneeAddress);
          saveLocalValue("goods_descriptions", h.goodsDescription);
        });
      });

      if (!silent) {
        setMessage({ text: "Saved successfully ✅", type: "success" });
      }
      await loadManifestList(true);
      return true;
    } catch (err) {
      setMessage({ text: err.message || "Server error", type: "error" });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedManifestId(null);
    setManifestQuery("");
    setManifest({
      general: {
        customsOfficeCode: DEFAULT_GENERAL.customsOfficeCode,
        voyageNumber: "",
        dateOfDeparture: "",
        dateOfArrival: "",
        storageStartDate: "",
        storageEndDate: "",
        storageDays: "0",
        storageRate: "0",
        totalBols: "0",
        totalPackages: "0",
        totalContainers: "0",
        totalGrossMass: "0",
        totalServiceCost: "0",
        totalStorageCharge: "0",
        totalInvoice: "0",
        modeOfTransportCode: DEFAULT_GENERAL.modeOfTransportCode,
        transporterNationality: DEFAULT_GENERAL.transporterNationality,
        placeOfDepartureCode: DEFAULT_GENERAL.placeOfDepartureCode,
        placeOfDestinationCode: DEFAULT_GENERAL.placeOfDestinationCode,
        carrierCode: "",
        carrierName: "",
        carrierAddress: "",
        shippingAgentCode: "",
        shippingAgentName: "",
        identityOfTransporter: "",
      },
      bols: [],
    });
    setXmlFile(null);
    setMode("manual");
  };

  return (
    <div className="screen screen-rtl">
      <style>{`
        .has-error input, 
        .has-error select, 
        .has-error .select-field select {
          border: 2px solid #ff4d4f !important;
        }
        .has-error label {
          color: #ff4d4f;
        }
      `}</style>
      <div className="screen-header">
        <h2>Export Manifest Data Entry (AWMDS)</h2>
        <p style={{ margin: 0, opacity: 0.8 }}>Export System: Cargo Management, Services, Storage Days, and Invoicing</p>
      </div>

      {/* ✅ Check AWB Status Section */}
      {/* <div className="card"> */}
        {/* <h3>Check AWB Status</h3>
        <div style={{ display: "flex", gap: "10px", alignItems: "flex-end" }}>
          <div className="field" style={{ flex: 1 }}>
            <label>AWB Reference</label>
            <input
              value={bolStatusQuery}
              onChange={(e) => setBolStatusQuery(e.target.value)}
              placeholder="Enter AWB Ref"
              onKeyDown={(e) => e.key === "Enter" && checkBolStatus()}
            />
          </div>
          <button type="button" onClick={checkBolStatus} disabled={bolStatusLoading}>
            {bolStatusLoading ? "Checking..." : "Check"}
          </button>
        </div> */}

        {bolStatusResult && (
          <div style={{ marginTop: "1rem" }}>
            {bolStatusResult.error ? (
              <div className="message error">{bolStatusResult.error}</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                {(Array.isArray(bolStatusResult) ? bolStatusResult : []).map((bol, idx) => {
                  let statusText = "";
                  let statusBg = "";
                  let statusColor = "";

                  if (bol.is_delivered) {
                    statusText = "Delivered ✅";
                    statusBg = "#e8f5e9";
                    statusColor = "#2e7d32";
                  } else if (bol.is_do_issued) {
                    statusText = "Delivery Order Issued (Not Delivered) 📜";
                    statusBg = "#fff3e0";
                    statusColor = "#ef6c00";
                  } else {
                    statusText = "Pending (No DO Issued) ⏳";
                    statusBg = "#ffebee";
                    statusColor = "#c62828";
                  }

                  return (
                    <div key={bol.id || idx} style={{ padding: "1rem", borderRadius: "8px", backgroundColor: statusBg, color: statusColor, border: `1px solid ${statusColor}` }}>
                      <h4 style={{ margin: "0 0 0.5rem 0" }}>{statusText}</h4>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "0.5rem", fontSize: "0.9rem", color: "#333" }}>
                        <div><strong>AWB:</strong> {bol.bol_reference}</div>
                        <div><strong>Consignee:</strong> {bol.consignee_name}</div>
                        <div><strong>Flight:</strong> {bol.flight_no || bol.voyage_number}</div>
                        <div><strong>Arrival:</strong> {bol.date_of_arrival ? String(bol.date_of_arrival).slice(0, 10) : ""}</div>
                        <div><strong>Pieces:</strong> {bol.number_of_packages}</div>
                        <div><strong>Weight:</strong> {bol.gross_mass}</div>
                        <div><strong>Registration:</strong> {bol.registration || ""}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      {/* </div> */}

      {canSearchManifest && (
        <div className="card">
          <h3>Search Manifest</h3>
          <div className="autocomplete">
            <input
              type="text"
              placeholder="Search by Manifest ID / Flight / Arrival Date"
              value={manifestQuery}
              onFocus={() => {
                loadManifestList();
                setShowManifestDropdown(true);
              }}
              onChange={(e) => handleManifestInputChange(e.target.value)}
            />
            {showManifestDropdown && (
              <ul className="dropdown-list">
                {filteredManifests.length === 0 && <li className="empty">No results</li>}
                {filteredManifests.map((m) => (
                  <li key={m.id} onClick={() => handleSelectManifest(m)}>
                    <strong>Manifest #{m.id}</strong>
                    <div className="hint">
                      Flight: {m.voyage_number || "—"} | Arrival: {m.date_of_arrival || "—"} | AWBs: {m.total_bols ?? "—"}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* ✅ واجهة اختيار البوالص المدفوعة */}
      <div className="card">
        <h3>Include Paid AWBs in Manifest</h3>
        <div className="autocomplete">
          <button 
            type="button" 
            onClick={() => setShowPaidBolsDropdown(!showPaidBolsDropdown)}
            style={{ width: '100%', textAlign: 'left', display: 'flex', justifyContent: 'space-between' }}
          >
            Select Paid AWB to add... <span>{showPaidBolsDropdown ? '▲' : '▼'}</span>
          </button>
{showPaidBolsDropdown && (
  <ul className="dropdown-list">
    {paidBols.length === 0 && (
      <li className="empty">No paid AWBs found</li>
    )}
    {paidBols
      // ✅ فلترة مزدوجة: من الـ state المحلي + من البوالص المضافة للمانيفست
      .filter(pb => 
        !manifest.bols.some(mb => 
          mb.bolReference === pb.bol_reference || 
          mb.id === pb.id
        )
      )
      .map((bol) => (
        <li key={bol.id} onClick={() => importPaidBol(bol)}>
          <strong>AWB: {bol.bol_reference}</strong>
          <div className="hint">
            Consignee: {bol.consignee_name} | Weight: {bol.gross_mass}kg
          </div>
        </li>
      ))
    }
  </ul>
)}
        </div>
      </div>

      <div className="mode-switch">
        <button
          type="button"
          className={mode === "xml" ? "active-tab" : ""}
          onClick={() => {
            resetForm();
            setSelectedManifestId(null);
            setManifestQuery("");
            setShowManifestDropdown(false);
            setMode("xml");
          }}
        >
          Load from XML
        </button>
        <button
          type="button"
          className={mode === "manual" ? "active-tab" : ""}
            onClick={() => {
    resetForm();          // 👈 Clear all fields
    setMode("manual");
  }}
        >
          Manual Entry
        </button>
      </div>

      {mode === "xml" && (
        <div className="xml-upload">
          <input type="file" accept=".xml" onChange={handleFileChange} />
          <button type="button" onClick={handleLoadFromXml}
          className={`interactive ${loading ? "loading" : ""}`}
          >
            Load data from file
          </button>
        </div>
      )}

 <div className="card">
  <div className="card-header-row">
    <h3>Manifest Header (Export)</h3>
    <button
      type="button"
      className="secondary-btn"
      onClick={() => setShowGeneral(v => !v)}
    >
      {showGeneral ? "Collapse ▲" : "Expand ▼"}
    </button>
  </div>

  {showGeneral && (
    <div className="grid grid-auto">
<Field label="Owner or Operator" error={showErrors && !manifest.general.ownerOperator}>
  <input
    value={manifest.general.ownerOperator || ""}
    onFocus={() => {
      setShowOwnerDropdown(true);
      // preload options
      setOwnerQuery(manifest.general.ownerOperator || "");
    }}
    onChange={(e) => {
      const v = e.target.value;
      handleGeneralChange("ownerOperator", v);
      setOwnerQuery(v);
    }}
  />

  {showOwnerDropdown && ownerOptions.length > 0 && (
    <ul className="dropdown-list">
      {ownerOptions.map((o, idx) => (
        <li
          key={o.owner_operator + "_" + idx}
          onMouseDown={() => {
            handleGeneralChange("ownerOperator", o.owner_operator || "");
            handleGeneralChange("customsOfficeCode", o.customs_office_code || "");
            setShowOwnerDropdown(false);
            setOwnerOptions([]);
          }}
        >
          <strong>{o.owner_operator}</strong>
          <div className="hint">{o.flight_no || "-"} | {o.customs_office_code || "-"} | {o.voyage_number || "-"}</div>
        </li>
      ))}
    </ul>
  )}
</Field>
          <Field label="Aircraft Registration" error={showErrors && !manifest.general.registration}>
            <input
              value={manifest.general.registration || ""}
              onChange={(e) => handleGeneralChange("registration", e.target.value)}
            />
          </Field>
                    <Field label="Declared Total AWBs" error={showErrors && !manifest.general.declaredTotalBols}>
                      <input
                        value={manifest.general.declaredTotalBols || ""}
                        onChange={(e) => handleGeneralChange("declaredTotalBols", e.target.value)}
                      />
                    </Field>
 <Field label="Customs Office Code" error={showErrors && !manifest.general.customsOfficeCode}>
  <input
    value={manifest.general.customsOfficeCode || ""}
onChange={(e) => {
  const v = e.target.value;
  handleGeneralChange("customsOfficeCode", v);
  setVoyageDraft(d => ({ ...d, customsOfficeCode: v }));
}}

  />
</Field>
<Field label="Voyage / Flight No." error={showErrors && !manifest.general.voyageNumber}>
  <input
    value={manifest.general.voyageNumber || ""}
    onChange={(e) => {
      const v = e.target.value;
      setVoyageQuery(v);
      handleGeneralChange("voyageNumber", v);
      setVoyageDraft(d => ({ ...d, voyageNumber: v }));
      setShowVoyageDropdown(true);
    }}
  />

  {showVoyageDropdown && voyageOptions.length > 0 && (
    <ul className="dropdown-list">
      {voyageOptions.map(v => (
        <li
          key={v.voyage_number}
          onMouseDown={() => {
            handleGeneralChange("voyageNumber", v.voyage_number);
            handleGeneralChange("ownerOperator", v.owner_operator || "");
            handleGeneralChange("customsOfficeCode", v.customs_office_code || "");
            setVoyageDraft({
              voyageNumber: v.voyage_number,
              ownerOperator: v.owner_operator || "",
              customsOfficeCode: v.customs_office_code || ""
            });
            setShowVoyageDropdown(false);
          }}
        >
          <strong>{v.voyage_number}</strong>
          <div className="hint">
            {v.owner_operator} | {v.customs_office_code}
          </div>
        </li>
      ))}
    </ul>
  )}
</Field>



          <Field label="Flight No." error={showErrors && !manifest.general.flightNo}>
            <input
              value={manifest.general.flightNo}
              onChange={(e) => handleGeneralChange("flightNo", e.target.value)}
            />
          </Field>
          <Field label="Date of Departure" error={showErrors && !manifest.general.dateOfArrival}>
            <input
              type="date"
              value={manifest.general.dateOfArrival}
              onChange={(e) => handleGeneralChange("dateOfArrival", e.target.value)}
            />
          </Field>
            {/* <Field label="Total Storage Charge">
              <input
                type="text"
                readOnly
                value={computedTotals.totalStorageCharge}
              />
            </Field> */}
            {/* <Field label="Estimated Invoice Amount">
              <input
                type="text"
                readOnly
                value={computedTotals.totalInvoice}
              />
            </Field> */}

            {/*
          </Field>
          <Field label="Total Number of AWB">
            <input value={manifest.general.totalBols} readOnly />
          </Field>
          <Field label="Total Number of Packages">
            <input value={manifest.general.totalPackages} readOnly />
          </Field>
          <Field label="Total Number of Containers">
            <input value={manifest.general.totalContainers} readOnly />
          </Field>
          <Field label="Total Gross Mass">
            <input value={manifest.general.totalGrossMass} readOnly />
          </Field>
          */}
          {/* <Field label="Mode of Transport Code" error={showErrors && !manifest.general.modeOfTransportCode}>
            <input
              value={manifest.general.modeOfTransportCode}
              onChange={(e) => handleGeneralChange("modeOfTransportCode", e.target.value)}
            />
          </Field> */}
          {/* <Field label="Transporter Nationality" error={showErrors && !manifest.general.transporterNationality}>
            <input
              value={manifest.general.transporterNationality}
              onChange={(e) => handleGeneralChange("transporterNationality", e.target.value)}
            />
          </Field> */}
          <Field label="Port of Loading (Origin)" error={showErrors && !manifest.general.placeOfDepartureCode}>
            <input
              value={manifest.general.placeOfDepartureCode}
              onChange={(e) => handlePlaceOfDepartureChange(e.target.value)}
              list="pointOfLoading-options"
            />
            <datalist id="pointOfLoading-options">
              {PREDEFINED_TRIPS.map(trip => (
                <option key={trip.pointOfLoading} value={trip.pointOfLoading} />
              ))}
            </datalist>
          </Field>
          <Field label="Port of Discharge (Destination)" error={showErrors && !manifest.general.placeOfDestinationCode}>
            <input
              value={manifest.general.placeOfDestinationCode || ""}
              onChange={(e) => handleGeneralChange("placeOfDestinationCode", e.target.value)}
            />
          </Field>
        </div>
      )}
      </div>

      <div className="card bol-card">
        <div className="card-header-row">
          <h3>AWB Data</h3>
          {/* <button type="button" onClick={handleAddBol}
          className={`interactive ${loading ? "loading" : ""}`}
          >
            + Add New AWB
          </button> */}
        </div>
        {manifest.bols.length === 0 && (
          <p className="hint-text">No AWB data yet. You can load from an XML file or add AWB manually.</p>
        )}
        <div className="bol-list">
          {manifest.bols.map((master, mIndex) => {
            if (master.hidden) return null;
            
            return (
            <div
              className={`bol-item ${focusedBol === `bol-item-${mIndex}` ? "focused" : ""}`}
              key={master.id || master.tempId || mIndex}
              id={`bol-item-${mIndex}`}
              style={{
                minHeight: focusedBol === `bol-item-${mIndex}` ? "80vh" : "auto",
                transition: "min-height 0.3s ease",
                borderLeft: "6px solid #2e7d32",
                background: "#fdfdfd"
              }}
              onClick={(e) => {
                if (master.collapsed) {
                   setManifest(prev => {
                      const bols = [...prev.bols];
                      bols[mIndex] = { ...bols[mIndex], collapsed: false };
                      return { ...prev, bols };
                   });
                }
                if (e.target.tagName !== "BUTTON") {
                   if (focusedBol !== `bol-item-${mIndex}`) {
                       setFocusedBol(`bol-item-${mIndex}`);
                       ignoreScrollRef.current = true;
                       e.currentTarget.scrollIntoView({ behavior: "smooth", block: "center" });
                       setTimeout(() => { ignoreScrollRef.current = false; }, 800);
                   }
                }
              }}
            >
<div className="card-header-row">
  <div className="bol-title"><span className="badge badge-gcr" style={{marginRight: '10px'}}>MASTER</span> AWB #{mIndex + 1}</div>

  <div style={{ display: "flex", gap: 8 }}>
                {master.is_offloaded && <span className="badge badge-dg">Offloaded</span>}

    {master.showSuccessIcon ? (
      <div style={{ display: "flex", alignItems: "center", color: "green", fontWeight: "bold", padding: "0 10px" }}>
        ✅ Saved
      </div>
    ) : (
    <button
      type="button"
      className="secondary-btn"
      onClick={async () => {
        const ok = await handleSave(true);
        if (ok) {
          setManifest((prev) => {
            const bols = [...prev.bols];
            bols[mIndex] = { ...bols[mIndex], showSuccessIcon: true };
            return { ...prev, bols };
          });
          setTimeout(() => {
            setManifest((prev) => {
              const bols = [...prev.bols];
              if (bols[mIndex]) {
                bols[mIndex] = { ...bols[mIndex], hidden: true, showSuccessIcon: false };
              }
              return { ...prev, bols };
            });
          }, 2000);
        }
      }}
    >
      Save & Hide
    </button>
    )}

    {/* <button type="button" onClick={() => handleAddHome(mIndex)}>
      + Add House
    </button> */}
    {/* ✅ Delete Master Button */}
    <button type="button" className="danger-btn" onClick={() => handleDeleteBol(mIndex)}>
      Delete
    </button>
  </div>
</div>
            {!master.collapsed && (
            <>
              <div className="grid grid-auto small-gap">
                <Field label="Cargo Type" error={showErrors && !master.cargoType}>
                  <div className="select-field">
                    <select
                      value={master.cargoType || ""}
                      disabled={true}
                      onChange={(e) => handleBolChange(mIndex, "cargoType", e.target.value)}
                    >
                      <option value="">-- Select --</option>
                      {cargoTypes.map((ct) => (
                        <option key={ct.id} value={ct.name}>{ct.name}</option>
                      ))}
                    </select>
                  </div>
                </Field>
                <Field label="AWB Reference" error={showErrors && !master.bolReference}>
                  <input
                    value={master.bolReference || ""}
                    disabled={master.isLocked}
                    onChange={(e) => handleBolChange(mIndex, "bolReference", e.target.value)}
                  />
                </Field>
                {/* <Field label="Flight Number">
                  <input
                    value={master.lineNumber || ""}
                    disabled={master.isLocked}
                    onChange={(e) => handleBolChange(mIndex, "lineNumber", e.target.value)}
                  />
                </Field> */}
<Field label="Nature of goods" error={showErrors && !master.bolNature}>
  <input
    value={master.bolNature || ""}
    disabled={master.isLocked}
    onChange={(e) => handleBolChange(mIndex, "bolNature", e.target.value)}
  />
</Field>
                <Field label="AWB Type Code" error={showErrors && !master.bolTypeCode}>
                  <input
                    value={master.bolTypeCode || ""}
                    disabled={master.isLocked}
                    onChange={(e) => handleBolChange(mIndex, "bolTypeCode", e.target.value)}
                  />
                </Field>
                {/* <Field label="Point of loading">
                  <input
                    value={master.placeOfLoadingCode}
                    disabled={master.isLocked}
                    onChange={(e) => handleBolChange(mIndex, "placeOfLoadingCode", e.target.value)}
                  />
                </Field> */}
                {/* <Field label="Place of Unloading Code">
                  <input
                    value={master.placeOfUnloadingCode}
                    disabled={master.isLocked}
                    onChange={(e) => handleBolChange(mIndex, "placeOfUnloadingCode", e.target.value)}
                  />
                </Field> */}
              </div>
              <div className="grid grid-auto small-gap">
                <Field label="Notify Name" error={showErrors && !master.notifyName}>
                  <input
                    value={master.notifyName || ""}
                    disabled={master.isLocked}
                    onChange={(e) => handleBolChange(mIndex, "notifyName", e.target.value)}
                  />
                </Field>
                <Field label="Notify Address" error={showErrors && !master.notifyAddress}>
                  <input
                    value={master.notifyAddress || ""}
                    disabled={master.isLocked}
                    onChange={(e) => handleBolChange(mIndex, "notifyAddress", e.target.value)}
                  />
                </Field>
              </div>
              <div className="grid grid-auto small-gap" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
                <Field label="Number of Packages" error={showErrors && !master.numberOfPackages}>
                  <input
                    value={master.numberOfPackages || ""}
                    disabled={true}
                    onChange={(e) => handleBolChange(mIndex, "numberOfPackages", e.target.value)}
                  />
                </Field>
                <Field label="Package Type Code" error={showErrors && !master.packageTypeCode}>
                  <input
                    value={master.packageTypeCode || ""}
                    disabled={master.isLocked}
                    onChange={(e) => handleBolChange(mIndex, "packageTypeCode", e.target.value)}
                  />
                </Field>
                <Field label="Gross weight" error={showErrors && !master.grossMass}>
                  <input
                    value={master.grossMass || ""}
                    disabled={true}
                    onChange={(e) => handleBolChange(mIndex, "grossMass", e.target.value)}
                  />
                </Field>
                <Field label="Storage Start Date" error={showErrors && !master.storageStartDate}>
                  <input
                    type="date"
                    value={master.storageStartDate || manifest.general.storageStartDate || ""}
                    disabled={master.isLocked}
                    onChange={(e) => handleBolChange(mIndex, "storageStartDate", e.target.value)}
                  />
                </Field>
                <Field label="Storage End Date" error={showErrors && !master.storageEndDate}>
                  <input
                    type="date"
                    value={master.storageEndDate || manifest.general.storageEndDate || ""}
                    disabled={master.isLocked}
                    onChange={(e) => handleBolChange(mIndex, "storageEndDate", e.target.value)}
                  />
                </Field>
                <Field label="Shipping Marks" error={showErrors && !master.shippingMarks}>
                  <input
                    value={master.shippingMarks || ""}
                    disabled={master.isLocked}
                    onChange={(e) => handleBolChange(mIndex, "shippingMarks", e.target.value)}
                  />
                </Field>
                <LocalAutocomplete
                  label="Goods Description"
                  storageKey="goods_descriptions"
                  value={master.goodsDescription}
                  error={showErrors && !master.goodsDescription}
                  disabled={master.isLocked}
                  onChange={(val) => handleBolChange(mIndex, "goodsDescription", val)}
                  onSelect={(s) => {
                    handleBolChange(mIndex, "goodsDescription", s.name);
                  }}
                />
                <Field label="Free Period">
                  <input value={freePeriods.find(fp => fp.type === master.cargoType)?.free_period || "None"} readOnly />
                </Field>
                <Field label="Storage Days">
                  <input value={calculateStorageFee(master, master.storageEndDate || master.storage_end_date || manifest.general.dateOfArrival || manifest.general.storageEndDate).days} readOnly />
                </Field>
                <Field label="Storage Fee">
                  <input value={calculateStorageFee(master, master.storageEndDate || master.storage_end_date || manifest.general.dateOfArrival || manifest.general.storageEndDate).fee.toFixed(2)} readOnly />
                </Field>
                 <div className="field-checkbox" style={{ alignSelf: "end", marginBottom: 8 }}>
                  <input
                    type="checkbox"
                    id={`day-${mIndex}`}
                    checked={master.day || false}
                    disabled={master.isLocked}
                    onChange={(e) => handleBolChange(mIndex, "day", e.target.checked)}
                  />
                  <label htmlFor={`day-${mIndex}`}>Subtract Day</label>
                </div>
                {/* <Field label="Number of Containers for this AWB">
                  <input
                    value={master.numOfCtnForThisBol}
                    disabled={master.isLocked}
                    onChange={(e) => handleBolChange(mIndex, "numOfCtnForThisBol", e.target.value)}
                  />
                </Field> */}
              </div>
            </>
            )}

              {(Array.isArray(master.homes) ? master.homes : []).map((home, hIndex) => {
                if (home.hidden) return null;

                return (
                <div
                  className={`bol-item ${focusedBol === `bol-item-${mIndex}-${hIndex}` ? "focused" : ""}`}
                  key={home.id || home.tempId || `${mIndex}-${hIndex}`}
                  id={`bol-item-${mIndex}-${hIndex}`}
                  style={{
                    marginTop: 12, 
                    borderLeft: "4px solid #0288d1",
                    marginLeft: "40px",
                    minHeight: focusedBol === `bol-item-${mIndex}-${hIndex}` ? "80vh" : "auto",
                    transition: "min-height 0.3s ease",
                    background: "#f8fafc"
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (home.collapsed) {
                       setManifest(prev => {
                          const bols = [...prev.bols];
                          const homes = [...bols[mIndex].homes];
                          homes[hIndex] = { ...homes[hIndex], collapsed: false };
                          bols[mIndex] = { ...bols[mIndex], homes };
                          return { ...prev, bols };
                       });
                    }
                    if (e.target.tagName !== "BUTTON") {
                       if (focusedBol !== `bol-item-${mIndex}-${hIndex}`) {
                           setFocusedBol(`bol-item-${mIndex}-${hIndex}`);
                           ignoreScrollRef.current = true;
                           e.currentTarget.scrollIntoView({ behavior: "smooth", block: "center" });
                           setTimeout(() => { ignoreScrollRef.current = false; }, 800);
                       }
                    }
                  }}
                >
                  <div className="bol-title">
  <span className="badge badge-avi" style={{marginRight: '10px', background: '#0288d1'}}>HOUSE</span> AWB #{mIndex + 1}.{hIndex + 1}
  {home.showSuccessIcon ? (
    <span style={{ marginLeft: 8, color: "green", fontWeight: "bold" }}>✅ Saved</span>
  ) : (
  <button
    type="button"
    className="secondary-btn"
    style={{ marginLeft: 8 }}
    onClick={async () => {
      const ok = await handleSave(true);
      if (ok) {
        setManifest((prev) => {
          const bols = [...prev.bols];
          const homes = [...bols[mIndex].homes];
          homes[hIndex] = { ...homes[hIndex], showSuccessIcon: true };
          bols[mIndex] = { ...bols[mIndex], homes };
          return { ...prev, bols };
        });
        setTimeout(() => {
          setManifest((prev) => {
            const bols = [...prev.bols];
            if (bols[mIndex] && bols[mIndex].homes && bols[mIndex].homes[hIndex]) {
              const homes = [...bols[mIndex].homes];
              homes[hIndex] = { ...homes[hIndex], hidden: true, showSuccessIcon: false };
              bols[mIndex] = { ...bols[mIndex], homes };
            }
            return { ...prev, bols };
          });
        }, 2000);
      }
    }}
  >
    Save & Hide
  </button>
  )}
  {/* ✅ Delete House Button */}
  <button type="button" className="danger-btn" style={{ marginLeft: 8 }} onClick={() => handleDeleteHome(mIndex, hIndex)}>
    Delete
  </button>
</div>
                  {!home.collapsed && (
                  <>
                  <div className="grid grid-auto small-gap">
                    <Field label="Cargo Type" error={showErrors && !home.cargoType}>
                      <div className="select-field">
                        <select
                          value={home.cargoType || ""}
                          disabled={true}
                          onChange={(e) => handleBolChange(mIndex, "cargoType", e.target.value, hIndex)}
                        >
                          <option value="">-- Select --</option>
                          {cargoTypes.map((ct) => (
                            <option key={ct.id} value={ct.name}>{ct.name}</option>
                          ))}
                        </select>
                      </div>
                    </Field>
                    <Field label="AWB Reference" error={showErrors && !home.bolReference}>
                      <input
                        value={home.bolReference}
                        disabled={home.isLocked}
                        onChange={(e) => handleBolChange(mIndex, "bolReference", e.target.value, hIndex)}
                      />
                    </Field>
                    {/* <Field label="Flight Number">
                      <input
                        value={home.lineNumber}
                        disabled={home.isLocked}
                        onChange={(e) => handleBolChange(mIndex, "lineNumber", e.target.value, hIndex)}
                      />
                    </Field> */}
                    <Field label="Nature of goods" error={showErrors && !home.bolNature}>
                      <input
                        value={home.bolNature || ""}
                        disabled={home.isLocked}
                        onChange={(e) => handleBolChange(mIndex, "bolNature", e.target.value, hIndex)}
                      />
                    </Field>
                    <Field label="AWB Type Code" error={showErrors && !home.bolTypeCode}>
                      <input
                        value={home.bolTypeCode}
                        disabled={home.isLocked}
                        onChange={(e) => handleBolChange(mIndex, "bolTypeCode", e.target.value, hIndex)}
                      />
                    </Field>
                    {/* <Field label="Point of loading">
                      <input
                        value={home.placeOfLoadingCode}
                        disabled={home.isLocked}
                        onChange={(e) => handleBolChange(mIndex, "placeOfLoadingCode", e.target.value, hIndex)}
                      />
                    </Field> */}
                    {/* <Field label="Place of Unloading Code">
                      <input
                        value={home.placeOfUnloadingCode}
                        disabled={home.isLocked}
                        onChange={(e) => handleBolChange(mIndex, "placeOfUnloadingCode", e.target.value, hIndex)}
                      />
                    </Field> */}
                  </div>
                  <div className="grid grid-auto small-gap">
                    <Field label="Notify Name" error={showErrors && !home.notifyName}>
                      <input
                        value={home.notifyName || ""}
                        disabled={home.isLocked}
                        onChange={(e) => handleBolChange(mIndex, "notifyName", e.target.value, hIndex)}
                      />
                    </Field>
                    <Field label="Notify Address" error={showErrors && !home.notifyAddress}>
                      <input
                        value={home.notifyAddress || ""}
                        disabled={home.isLocked}
                        onChange={(e) => handleBolChange(mIndex, "notifyAddress", e.target.value, hIndex)}
                      />
                    </Field>
                  </div>
                  <div className="grid grid-auto small-gap" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
                    <Field label="Number of Packages">
                      <input
                        value={home.numberOfPackages || ""}
                        disabled={true}
                        onChange={(e) => handleBolChange(mIndex, "numberOfPackages", e.target.value, hIndex)}
                      />
                    </Field>
                    <Field label="Package Type Code">
                      <input
                        value={home.packageTypeCode || ""}
                        disabled={home.isLocked}
                        onChange={(e) => handleBolChange(mIndex, "packageTypeCode", e.target.value, hIndex)}
                      />
                    </Field>
                    <Field label="Gross weight">
                      <input
                        value={home.grossMass || ""}
                        disabled={true}
                        onChange={(e) => handleBolChange(mIndex, "grossMass", e.target.value, hIndex)}
                      />
                    </Field>
                    <Field label="Shipping Marks">
                      <input
                        value={home.shippingMarks || ""}
                        disabled={home.isLocked}
                        onChange={(e) => handleBolChange(mIndex, "shippingMarks", e.target.value, hIndex)}
                      />
                    </Field>
                    {/* Display House Services */}
                    <Field label="House Services">
                      <div className="selected-items-container">
                        {home.services && home.services.length > 0 ? (
                          home.services.map(srvc => (
                            <span key={srvc.id} className="item-chip">
                              {srvc.type} - {srvc.classification}
                            </span>
                          ))
                        ) : (
                          <span className="hint-text">No services assigned</span>
                        )}
                      </div>
                    </Field>
                    {/* Display House Packing Items */}
                    <Field label="House Packing Items">
                      <div className="selected-items-container">
                        {home.packingItems && home.packingItems.length > 0 ? (
                          home.packingItems.map(item => (
                            <span key={item.id} className="item-chip">{item.type}</span>
                          ))
                        ) : (
                          <span className="hint-text">No packing items assigned</span>
                        )}
                      </div>
                    </Field>
                    {/* Display House Build-up Items */}
                    <Field label="House Build-up Items">
                      <div className="selected-items-container">
                        {home.buildUpItems && home.buildUpItems.length > 0 ? (
                          home.buildUpItems.map(item => (<span key={item.id} className="item-chip">{item.name}</span>))
                        ) : (<span className="hint-text">No build-up items assigned</span>)}
                      </div>
                    </Field>
                    <LocalAutocomplete
                      label="Goods Description"
                      storageKey="goods_descriptions"
                      value={home.goodsDescription}
                      disabled={home.isLocked}
                      onChange={(val) => handleBolChange(mIndex, "goodsDescription", val, hIndex)}
                      onSelect={(s) => {
                        handleBolChange(mIndex, "goodsDescription", s.name, hIndex);
                      }}
                    />
                  <Field label="Free Period">
                    <input value={freePeriods.find(fp => fp.type === home.cargoType)?.free_period || "None"} readOnly />
                  </Field>
                    <Field label="Storage Days">
                    <input value={calculateStorageFee(home, home.storageEndDate || home.storage_end_date || manifest.general.dateOfArrival || manifest.general.storageEndDate).days} readOnly />
                    </Field>
                    <Field label="Storage Fee">
                    <input value={calculateStorageFee(home, home.storageEndDate || home.storage_end_date || manifest.general.dateOfArrival || manifest.general.storageEndDate).fee.toFixed(2)} readOnly />
                    </Field>
                             <div className="field-checkbox" style={{ alignSelf: "end", marginBottom: 8 }}>
                      <input
                        type="checkbox"
                        id={`day-${mIndex}-${hIndex}`}
                        checked={home.day || false}
                        disabled={home.isLocked}
                        onChange={(e) => handleBolChange(mIndex, "day", e.target.checked, hIndex)}
                      />
                      <label htmlFor={`day-${mIndex}-${hIndex}`}>Subtract Day</label>
                    </div>
                    {/* <Field label="Number of Containers for this AWB">
                      <input
                        value={home.numOfCtnForThisBol}
                        disabled={home.isLocked}
                        onChange={(e) => handleBolChange(mIndex, "numOfCtnForThisBol", e.target.value, hIndex)}
                      />
                    </Field> */}
                  </div>
                  </>
)}
                </div>
                

                );
              })}

            </div>


            );
          })}

        </div>

      </div>

      {message.text && (
        <div className={`message ${message.type === "error" ? "error" : "success"}`}>
          {message.text}
        </div>
      )}

      <div className="actions-row" style={{ display: "flex", gap: "10px" }}>
        {selectedManifestId && !manifest.general.is_departed && (
          <button type="button" className="btn-primary" onClick={handleTakeOff}>
            🚀 Confirm Flight Takeoff
          </button>
        )}

      <button
        type="button"
        className="secondary-btn"
        onClick={() => {
          const user = JSON.parse(localStorage.getItem("user") || "{}");
          // استبعاد البوالص التي تم عمل Offload لها من الطباعة
          const printableBols = flattenBols(manifest.bols).filter(b => !b.is_offloaded);
          generateAirCargoManifestPdf({
            header: { ...manifest.general, id: selectedManifestId },
            bols: printableBols,
            totals: computeTotalsFromBols(manifest.bols),
            user
          });
        }}
        disabled={!selectedManifestId && manifest.bols.length === 0}
      >
        Print Air Cargo Manifest
      </button>
      <button
        type="button"
        className="secondary-btn"
        onClick={() => {
          const user = JSON.parse(localStorage.getItem("user") || "{}");
          // استبعاد البوالص التي تم عمل Offload لها من الطباعة
          const printableBols = flattenBols(manifest.bols).filter(b => !b.is_offloaded);
          generateAirCargoManifestPdf({
            header: { ...manifest.general, id: selectedManifestId },
            bols: printableBols,
            totals: computeTotalsFromBols(manifest.bols),
            user,
            title: "LOAD BREAKDOWN SHEET",
            fileNamePrefix: "LoadBreakdownSheet"
          });
        }}
        disabled={!selectedManifestId && manifest.bols.length === 0}
      >
        Print Load Breakdown Sheet
      </button>
        
        <button
          type="button"
          className="secondary-btn"
          onClick={handleExportXml}
        >
          Export XML
        </button>
<button
  type="button"
  onClick={async () => {
        const ok = await handleSave();
        if (ok) resetForm();
  }}
  disabled={loading || (selectedManifestId ? !canEditManifest : !canCreateManifest)}
>
  {loading ? "Saving..." : "Save to Database"}
</button>

      </div>
    </div>
  );
}

export default ManifestScreen;
